/* eslint-disable @typescript-eslint/no-unused-vars */
import debounce from "lodash/debounce";
import {
  Database as fbDatabase,
  serverTimestamp,
  ref,
  get,
  child,
  set,
  update,
  remove,
  Unsubscribe,
  push,
  DatabaseReference,
  onValue,
  DataSnapshot,
  goOffline,
  goOnline,
} from "firebase/database";
import {
  getDate,
  getTime,
  isAfter,
  subWeeks,
  differenceInDays,
  isBefore,
  addMinutes,
  subYears,
} from "date-fns";
import {
  sortKeys,
  decrypt,
  encrypt,
  getOwnerUserId,
  memberOwnerKeySuffix,
  roughSizeOfObject,
  withTimeout,
  sleep,
  cloneObject,
} from "./utils";
import {
  ISessionSettings,
  AuditMessage,
  IAuditLogsDateCollection,
  IMemberSentReceipt,
  ISystemConfig,
  IMemberMessage,
  MessageContents,
  ControlRecord,
  IUserRecord,
  IOwnerProfileSession,
  IAuditScoreHistory,
  ITaskItem,
  IControl,
  IOwnerHistory,
  PilloryTopic,
  VoteTask,
  ILockReceipt,
  IPilloryTopic,
  ISettingsItem,
  ICategoryItem,
  IOwnershipControl,
  ISessionProgressState,
  IPendingItem,
  IVoteTask,
  IAttendanceRequest,
  ICustomSchedule,
  IShoppingRequest as IShoppingRequest,
  IKeyedItem,
  ITodoItem,
  IRequestLockState,
  IGame,
  ITaskRating,
  IWizardTemplate,
  IWeeklyTargetProgress,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  EventType,
  HygieneBreak,
  ItemTypes,
  OwnerType,
  PenaltyPointConversionType,
  PendingUpdateType,
  ReportStyle,
  TraceLevel,
  WeekendHandling,
} from "../ts/enums";
import { MessageType, NotificationType } from "../controllers/messaging";
import { getAnalytics, isSupported, logEvent } from "firebase/analytics";
import {
  applyStructuredSubscriptionPatch,
  buildStructuredSettingsSnapshot,
  buildStructuredSettingsUpdates,
  decodeSettingsSnapshot,
  getStructuredSettingsSubscriptionKeys,
  hasStructuredSettingsData,
  structuredSnapshotNeedsLegacyFallback,
  mergeSettings,
  setNewItemsToHandled,
  shouldBackfillStructuredSettings,
  shouldRequestLegacySettingsFallback,
  shouldUseStructuredSettingsSnapshot,
  stripImagesFromSettings,
  stripSettings,
  hydrateItemFromCreator,
  isCreatorItemHandled,
} from "./settingsManager";
import { UserCredential } from "firebase/auth";
import Emitter, {
  DATABASE_CHECK_SAVING,
  DATABASE_CONNECTING,
  DATABASE_OFFLINE,
  DATABASE_ONLINE,
  DATABASE_SAVE_COMPLETE,
  DATABASE_SAVE_PENDING,
} from "./Emitter";

import { v5 } from "uuid";
import { getLocation } from "./externalApiUtils";
import { formatValidDate } from "./dateUtils";
import { deleteFilePath, readJsonFile, writeJsonFile } from './offlineModeHelper'; // Import the utility functions

export const STATE_KEY_SESSION_ID = "session_key";

const ShoppingItemPath = "purchaseRequests";
const TodoItemPath = "todos";
const GamePath = "games";
const GameSummaryPath = "gameSummaries";
const WizardTemplatePath = "wizardTemplates";
const WeeklyTargetProgressPath = "weeklyTargetProgress";

/** Extract only the IGame base fields for lightweight summary storage (no subtype-specific data). */
const toGameSummary = (game: IGame): IGame => {
  const summary: IGame = {
    key: game.key,
    name: game.name,
    description: game.description,
    startTime: game.startTime,
    completed: game.completed,
    userid: game.userid,
    username: game.username,
    gameType: game.gameType,
  };
  if (game.endTime !== undefined) summary.endTime = game.endTime;
  if (game.img !== undefined) summary.img = game.img;
  if (game.imgId !== undefined) summary.imgId = game.imgId;
  if (game.allowedMistakes !== undefined) summary.allowedMistakes = game.allowedMistakes;
  if (game.allowedTimeMinutes !== undefined) summary.allowedTimeMinutes = game.allowedTimeMinutes;
  return summary;
};

// TEMPORARY: cold-start load performance logging — remove before next deploy
const loadPerf = (label: string, ...extra: unknown[]) => {
  if (typeof performance === 'undefined') return;
  if (import.meta.env.VITE_ENABLE_LOAD_PERF !== 'true') return;
  const g = globalThis as any;
  if (!g.__loadPerfT0) g.__loadPerfT0 = performance.now();
  console.log(`[LOAD_PERF] +${(performance.now() - g.__loadPerfT0).toFixed(0)}ms | ${label}`, ...extra);
};

export enum DeleteMessageMode {
  All,
  Read,
  Housekeep,
}

export const defaultSettings: ISessionSettings = {
  // ability to control the application background for member remotely
  backgroundColor: "#26272e",
  // title to show in application instead of logo
  applicationTitle: "",
  // optional cross-day rules shown in schedule shortcuts
  dailyRulesText: "",
  // default attrition cost per day
  dailyCost: 1.5,
  // should the user have to verify their compliance
  randomVerifications: false,
  // randomise settings on daily roll
  randomiseSettings: false,
  // show count down of time remaining
  showRemainingTimes: true,
  // show bank balance
  showBank: true,
  // earliest report can be requested
  minReportTime: "20:00",
  // max time for report to be requested
  maxReportTime: "22:00",
  // user word count expected
  reportWordCount: 500,
  // user forced to report
  dailyReporting: true,
  // state for the evening reports required
  dailyReportRequest: undefined,
  // state for the morning reports required
  morningReportRequest: undefined,
  // reward for completing the evening report
  dailyReportingCredit: 0.5,
  // by default twice a day
  reportStyle: ReportStyle.twice,
  // how to deal with weekends by default
  weekendHandling: WeekendHandling.normal,
  // how the penalties accrued should apply to the session
  penaltyPointConversionType: PenaltyPointConversionType.penalties,
  // how many times a month can member unlock and relock
  hygieneBreaks: HygieneBreak.none,
  // default intensity preset for newly created lines tasks
  linesGameplayModeDefault: "balanced",
  siteEffectsSettings: {
    backcolour: "#000000",
    backgroundModes: ["arms", "rings"],
    siteFxCategories: ["all"],
    images: {
      enabled: false,
      clickable: true,
      corruption: false,
      urls: [],
      perMin: 2,
      imagesPerFlash: 3,
      maxOnScreen: 8,
      imageDurationSec: 30,
    },
    videos: {
      enabled: false,
      strictLock: false,
      urls: [],
      perHour: 2,
      miniGameEnabled: false,
      miniGameTargets: 4,
      miniGameDurationSec: 10,
      miniGameTargetSizePx: 72,
      miniGameButtonMessages: ["Tap now", "Still watching?", "Click to continue"],
    },
    audio: {
      enabled: false,
      perHour: 2,
      urls: [],
    },
    messages: {
      enabled: false,
      messages: [],
      perMin: 2,
      frames: 3,
      opacityPercent: 90,
      messageDurationMinMs: 200,
      messageDurationMaxMs: 200,
    },
  },
  creditSettings: {
    // stop users adding their own items
    disableMemberAdditions: false,
    // the random range of count applied to a credit item
    creditCountVariance: 0.25,
    // the random range of credit received on completion of an item or position or task
    creditVariance: 0.25,
    // number of credits randomly chosen per day to complete
    dailyMandatoryCredits: 1,
    // the array of credits available
    credits: [],
    // should system tasks be disabled and only owner add items - only makes sense when you are owned
    disableSystemTasks: false,
    // categories that the random task selection process will not select from when picking daily mandatory items, or a random task on request
    singleItemCategories: [],
  },
  penaltySettings: {
    penalties: [],
  },
  permissionSettings: {
    disablePermissions: false,
    // when permissions have count, the random rang count can apply
    permissionDurationVariance: 0.5,
    //the array of permissions available
    permissions: [],
  },
  eventsSettings: {
    disableEvents: false,
    // when permissions have count, the random range count can apply
    permissionDurationVariance: 0.5,
    // the array of events available
    events: [],
  },
  positionSettings: {
    disablePositions: false,
    // the minimum times a position will occur a day
    minPositionCount: 1,
    // the maximum times a position will occur a day
    maxPositionCount: 5,
    // maximum minutes for a position
    positionUpperDuration: 10,
    // minimum minutes for a position
    positionLowerDuration: 1,
    // credits for completing total number of positions for a day
    positionCompleteCredit: 0.5,
    // the array of positions available
    positions: [],
  },
};

const defaultState = {
  bank: 0,
  currentDay: 0,
  credits: {},
  events: {},
  positions: {},
  tasks: {},
  outOfOffice: false,
  scheduler: [],
  isAdminMode: false,
};
export interface IDownloadLogEntry {
  caller: string;
  size: number;
  logTime: string;
}

// this.database interacts with the schema and updates relations and checks integrity
export default class Database {
  private DATABASE_UPDATE_MS = 200; // time between message pumps to save changes to the firebase database
  private DATABASE_RECONNECT_MS = 2000; // time to lay idle between reconnection attempts when disconnected
  private DATABASE_CONNECT_DELAY_MS = 1000; // time to wait after goOnline before interating with the server again
  private DATABASE_TRANSACTION_TIMEOUT = 5000; // how long to wait for transaction responses before declaring offline
  private static systemUserName = "system";
  private systemUser?: IControl;
  public database: fbDatabase;
  private systemSettings?: ISessionSettings;
  private userRecord?: IControl;
  private userCache: Record<string, IControl> | undefined = undefined;
  static totalDownloaded = 0;
  private downloadLog: IDownloadLogEntry[] = [];
  private logSizeCallbacks: ((args: {
    size: number;
    reason: string;
  }) => void)[] = [];
  public addLogSizeCallback = (
    cb: (args: { size: number; reason: string }) => void
  ) => {
    this.logSizeCallbacks.push(cb);
    let totalSize = 0;
    if (this.downloadLog.length > 0) {
      this.downloadLog.forEach((msg) => {
        totalSize += msg.size;
        cb({ size: totalSize, reason: msg.caller });
      });
    }
  };

  public removeLogSizeCallback = (
    cb: (args: { size: number; reason: string }) => void
  ) => {
    const idx = this.logSizeCallbacks.indexOf(cb);
    if (idx > -1) this.logSizeCallbacks.splice(idx, 1);
  };

  // Per-call sizes in kb; the logSize callbacks only report a running total.
  public getDownloadLog = (): IDownloadLogEntry[] => this.downloadLog;

  private sessionId: string | undefined;
  private offlineMode: boolean;
  private structuredBackfillInFlight = new Set<string>();

  constructor(database: fbDatabase, offlineMode = false) {
    this.database = database;
    this.offlineMode = offlineMode;
  }

  public logSize = (caller: string, size: number) => {
    this.downloadLog.push({
      caller,
      size,
      logTime: formatValidDate(new Date(), "dd MMM yyyy HH:mm:ss"),
    });
    if (size > 500)
      console.error(
        `${caller}: ${size}kb ${this.userRecord ? `(${this.userRecord?.username})` : ""
        }`
      );
    else
      console.debug(
        `${caller}: ${size}kb ${this.userRecord ? `(${this.userRecord?.username})` : ""
        }`
      );
    Database.totalDownloaded += size;
    const totalSize =
      Math.round((Database.totalDownloaded / 1000 + Number.EPSILON) * 100) /
      100;
    // console.debug(`Total Download [${totalSize}mb]`);
    this.logSizeCallbacks.forEach((cb) =>
      cb({ size: totalSize, reason: caller })
    );
    // const updates: Record<string, unknown> = {};
    // const key = `/downloads/${new Date().getDate()}/${this.sessionId}`;
    // updates[`${key}/calls`] = this.downloadLog;
    // updates[`${key}/total`] = totalSize;
    // if (this.userRecord) updates[`${key}/user`] = this.userRecord?.username ?? '';
    // updates[`${key}/lastLog`] = formatValidDate(new Date(), 'dd MMM yyyy HH:mm:ss');
    // updates[`${key}/version`] = import.meta.env.VITE_VERSION;
    // void this.update(updates);
  };

  public getSystemUser = () => {
    if (this.systemUser?.userid && this.allControlRecords[this.systemUser.userid]) {
      return this.allControlRecords[this.systemUser.userid];
    }
    return this.systemUser;
  };

  private backfillStructuredSettings = async (
    userid: string,
    settings: ISessionSettings
  ): Promise<void> => {
    if (!userid || !settings || this.structuredBackfillInFlight.has(userid)) return;
    this.structuredBackfillInFlight.add(userid);
    try {
      const migratedSettings = Database.migrateSettings(
        userid,
        cloneObject(settings) as ISessionSettings,
        this
      );
      await set(
        ref(this.database, "settingsV2/" + userid),
        buildStructuredSettingsSnapshot(migratedSettings)
      );
    } finally {
      this.structuredBackfillInFlight.delete(userid);
    }
  };
  public setCurrentUser = (userRecord: IControl) => {
    this.userRecord = userRecord;
  };

  public initialise = async (): Promise<void> => {
    // await this.getSystemSettings()
  };

  private getTimestamp(): unknown {
    return serverTimestamp() ?? new Date();
  }

  private pendingResponse = { pending: true };
  private subscriptionCallbacks: Record<string, ((snapshot: any) => void)[]> =
    {};
  private subscriptionCache: Record<string, string> = {};
  private subscriptionUnsubscribers: Record<string, Unsubscribe> = {};
  private valuesCache: Record<string, any> = {};
  private lockedQueries: Record<string, ((val: any) => void)[]> = {};
  private onSubscriptionPathUpdated = (snapshot: DataSnapshot) => {
    const vals = snapshot.val();
    const path = snapshot.ref.toString();
    this.valuesCache[path] = vals;
    if (this.subscriptionCallbacks[path])
      this.subscriptionCallbacks[path].forEach((cb) => {
        cb(vals);
      });
  };
  // this is more expensive and always goes to the db
  public async getTableSnap<T>(
    dataPath: string,
    bypassCache?: boolean
  ): Promise<T> {
    if (this.offlineMode) {
      const filePath = dataPath;
      return await readJsonFile(filePath);
    } else {
      const tableRef = ref(this.database);
      const childRef = child(tableRef, dataPath);
      const path = childRef.toString();
      if (!bypassCache && Object.keys(this.valuesCache).includes(path)) {
        if (this.valuesCache[path] !== this.pendingResponse)
          return this.valuesCache[path];
        else {
          // this is where the subscription has come in first but no result has come back yet but one might come back
          const promise = new Promise<any>((resolve) => {
            const cb = (val: any) => {
              resolve(val);
            };
            if (!this.subscriptionCallbacks[path])
              this.subscriptionCallbacks[path] = [];
            this.subscriptionCallbacks[path].push(cb);
          });
          const val = await promise;
          return val;
        }
      }

      if (this.lockedQueries[path] && !bypassCache) {
        const promise = new Promise<any>((resolve) => {
          const cb = (val: any) => {
            resolve(val);
          };
          this.lockedQueries[path].push(cb);
        });
        const val = await promise;
        return val;
      }

      this.lockedQueries[path] = [];
      return await get(childRef).then(
        (snapshot) => {
          const val = snapshot.val() as T;
          if (this.lockedQueries[path]) {
            const cbs = this.lockedQueries[path];
            delete this.lockedQueries[path];
            cbs.forEach((cb) => cb(val));
          }
          this.onSubscriptionPathUpdated(snapshot);
          this.logSize(dataPath + " getTableSnap", roughSizeOfObject(val));
          // getTableSnap is intentionally a one-time read — no live subscription.
          // Live updates for the path are handled by callers that use on().
          return val;
        },
        (e) => {
          void this.pushMessageToUser(
            this.systemUser?.userid as string,
            this.systemUser?.userid as string,
            this.systemUser?.username as string,
            e.toString(),
            "exception snapping database",
            this.systemUser?.username as string,
            MessageType.MemberMessage
          );
          const def: T = undefined as any as T;
          return def;
        }
      );
    }
  }

  public on = (
    query: DatabaseReference,
    cb: (data: any) => unknown
  ): (() => void) => {
    if (this.offlineMode) {
      const filePath = (query as any)._path.toString();
      const data = readJsonFile(filePath);
      cb(data);
      return () => { };
    } else {
      const path = query.toString();
      if (!this.subscriptionCallbacks[path])
        this.subscriptionCallbacks[path] = [cb];
      else this.subscriptionCallbacks[path].push(cb);
      if (
        Object.prototype.hasOwnProperty.call(this.valuesCache, path) &&
        this.valuesCache[path] !== this.pendingResponse
      ) {
        cb(this.valuesCache[path]);
      }
      if (!this.subscriptionCache[path]) {
        this.subscriptionCache[path] = path;
        // If valuesCache already has real data (from a prior getTableSnap), the
        // first onValue emission will be the same payload — suppress its log to
        // avoid double-counting a single wire download.
        const suppressFirstFire =
          Object.prototype.hasOwnProperty.call(this.valuesCache, path) &&
          this.valuesCache[path] !== this.pendingResponse;
        let firstFire = suppressFirstFire;
        let loggedSubscribe = suppressFirstFire;
        this.valuesCache[path] = this.pendingResponse;
        this.subscriptionUnsubscribers[path] = onValue(query, (snapshot) => {
          if (firstFire) {
            firstFire = false; // same data as getTableSnap, already logged there
          } else if (!loggedSubscribe) {
            loggedSubscribe = true;
            this.logSize(
              query.toString() + " onSubscribe",
              roughSizeOfObject(snapshot.val())
            );
          } else {
            // A value event only carries the changed subtree, which the SDK does not
            // expose. Count the churn but do not attribute the whole node to it.
            this.logSize(query.toString() + " onUpdate", 0);
          }
          this.onSubscriptionPathUpdated(snapshot);
        });
      }
      return () => {
        const callbacks = this.subscriptionCallbacks[path];
        if (!callbacks) return;
        const idx = callbacks.indexOf(cb);
        if (idx > -1) callbacks.splice(idx, 1);
        if (callbacks.length === 0) this.detachSubscription(path);
      };
    }
  };

  // Releases the underlying Firebase listener once nothing is consuming the path,
  // otherwise every path ever visited keeps streaming for the life of the tab.
  private detachSubscription = (path: string) => {
    const unsubscribe = this.subscriptionUnsubscribers[path];
    if (unsubscribe) unsubscribe();
    delete this.subscriptionUnsubscribers[path];
    delete this.subscriptionCallbacks[path];
    delete this.subscriptionCache[path];
    delete this.valuesCache[path]; // no longer kept live, so it must not serve getTableSnap
  };

  private checkIntegrity = async (): Promise<boolean> => {
    if (this.isDisconnected) return false;
    if (Database.performIntegrityCheck && this.sessionId && this.userRecord) {
      // console.log("checking session lock is valid still");
      const tableRef = ref(this.database);
      const childRef = child(
        tableRef,
        "/users/" + this.userRecord?.userid + "/state/" + STATE_KEY_SESSION_ID
      );
      await get(childRef).then((result) => {
        const integrityRef = result.val() as string;
        if (integrityRef && integrityRef !== this.sessionId) {
          // console.log("session was taken over by other device");
          // reload the browser
          Emitter.emitNotification({
            message:
              "Another session has started on a device. Refresh the browser to restart session",
            persist: true,
            error: true,
            fatal: true,
            notificationType: NotificationType.ErrorInfo,
          });
          window.location.replace("forceSignIn");
        } else {
          // console.log("session lock still valid");
        }
        Database.performIntegrityCheck = false;
      });
    }
    return true;
  };

  private timerHandle: NodeJS.Timeout | undefined = undefined;
  private disconnectHandle: NodeJS.Timeout | undefined = undefined;
  private checkingOnlineCommenced = false;

  private handleDisconnection = () => {
    // console.log('handling disconnection');
    if (this.disconnectHandle) return;
    goOffline(this.database);
    this.isDisconnected = true;
    Emitter.emit(DATABASE_OFFLINE);
    Database.performIntegrityCheck = true;
    // if not connected, let's try to connect again in 10 secs
    // console.log('queuing up reconnect attempt');
    this.disconnectHandle = setTimeout(
      this.checkConnected,
      this.DATABASE_RECONNECT_MS
    );
  };

  private checkConnected = async (): Promise<boolean> => {
    delete this.disconnectHandle;
    if (this.checkingOnlineCommenced) return false;
    if (!this.isDisconnected) return true;

    this.checkingOnlineCommenced = true;
    // console.log('attempting going online');
    Emitter.emit(DATABASE_CONNECTING);
    goOnline(this.database);
    await sleep(this.DATABASE_CONNECT_DELAY_MS);
    const tableRef = ref(this.database);
    const childRef = child(tableRef, "/onlineState/" + this.userRecord?.userid);
    let connected = false;
    const fetchPromise = get(childRef).then(() => (connected = true));
    // console.log('checking online tables');
    await withTimeout(this.DATABASE_TRANSACTION_TIMEOUT, fetchPromise).catch(
      () => (connected = false)
    );
    if (connected) {
      // console.log('now online');
      this.checkingOnlineCommenced = false;
      this.isDisconnected = false;
      Emitter.emit(DATABASE_ONLINE);
      void this.databaseUpdateThread();
    } else {
      // console.log('still offline - try again in 10 secs');
      this.disconnectHandle = setTimeout(() => {
        this.checkingOnlineCommenced = false;
        void this.checkConnected();
      }, this.DATABASE_RECONNECT_MS);
    }

    return !this.isDisconnected;
  };

  private static isAncestorPath = (ancestor: string, descendant: string): boolean => {
    return descendant.startsWith(`${ancestor}/`);
  };

  private static setNestedUpdateValue = (
    target: Record<string, unknown>,
    relativePath: string,
    value: unknown
  ): void => {
    const segments = relativePath.split('/').filter(Boolean);
    if (segments.length === 0) return;

    let current: Record<string, unknown> = target;
    for (let idx = 0; idx < segments.length - 1; idx += 1) {
      const segment = segments[idx];
      const next = current[segment];
      if (!next || typeof next !== 'object' || Array.isArray(next)) {
        current[segment] = {};
      }
      current = current[segment] as Record<string, unknown>;
    }

    current[segments[segments.length - 1]] = value;
  };

  public static mergeQueuedUpdateObjects = (
    existing: Record<string, unknown> = {},
    incoming: Record<string, unknown> = {}
  ): Record<string, unknown> => {
    const merged = { ...existing };

    Object.entries(incoming).forEach(([path, value]) => {
      const descendantKeys = Object.keys(merged).filter((key) =>
        key !== path && Database.isAncestorPath(path, key)
      );
      descendantKeys.forEach((key) => delete merged[key]);

      const ancestorKey = Object.keys(merged)
        .filter((key) => key !== path && Database.isAncestorPath(key, path))
        .sort((a, b) => b.length - a.length)[0];

      if (ancestorKey) {
        const ancestorValue = merged[ancestorKey];
        const relativePath = path.slice(ancestorKey.length + 1);
        if (ancestorValue && typeof ancestorValue === 'object' && !Array.isArray(ancestorValue)) {
          const clonedAncestor = cloneObject(ancestorValue as Record<string, unknown>) as Record<string, unknown>;
          Database.setNestedUpdateValue(clonedAncestor, relativePath, value);
          merged[ancestorKey] = clonedAncestor;
        } else {
          delete merged[ancestorKey];
          merged[path] = value;
        }
        return;
      }

      merged[path] = value;
    });

    return merged;
  };

  private lastRan: Date | undefined;
  private isDisconnected = false;
  public static performIntegrityCheck = false;
  private pendingActions: Array<{
    type: "set" | "remove" | "update";
    ref: DatabaseReference;
    obj?: any;
    resolvers: Array<() => void>;
  }> = [];
  private databaseUpdateThread = async () => {
    Emitter.emit(DATABASE_CHECK_SAVING);
    if (this.lastRan && isBefore(this.lastRan, addMinutes(new Date(), -1))) {
      if (!(await this.checkIntegrity())) {
        if (this.userRecord)
          await this.auditUser(
            [
              new AuditMessage(
                AuditActionTypes.Session,
                "Integrity Checks",
                "User found to have multiple sessions, throwing awaying any pending changes",
                TraceLevel.error
              ),
            ],
            this.userRecord
          );
        window.location.replace("forceSignIn");
      }
    }
    this.lastRan = new Date();
    if (this.pendingActions.length === 0) {
      Emitter.emit(DATABASE_SAVE_COMPLETE);
      this.timerHandle = undefined;
      return;
    }
    if (this.isDisconnected && !(await this.checkConnected())) return;
    if (Database.performIntegrityCheck && !(await this.checkIntegrity())) return;

    while (this.pendingActions.length > 0) {
      const action = this.pendingActions[0];
      let fbOp: Promise<void>;
      switch (action.type) {
        case "set": fbOp = set(action.ref, action.obj); break;
        case "remove": fbOp = remove(action.ref); break;
        default: fbOp = update(action.ref, action.obj); break;
      }
      let succeeded = false;
      // eslint-disable-next-line no-await-in-loop
      await withTimeout(this.DATABASE_TRANSACTION_TIMEOUT, fbOp)
        .then(() => { succeeded = true; })
        .catch(() => { this.handleDisconnection(); });
      if (!succeeded) break;
      this.pendingActions.shift();
      action.resolvers.forEach(r => r());
    }

    if (!this.isDisconnected)
      this.timerHandle = setTimeout(this.databaseUpdateThread, this.DATABASE_UPDATE_MS);
    else
      delete this.timerHandle;
  };

  private set = async (ref: DatabaseReference, obj: any): Promise<void> => {
    Emitter.emit(DATABASE_SAVE_PENDING);
    if (this.offlineMode) {
      const filePath = (ref as any)._path.toString();
      await writeJsonFile(filePath, obj);
      return;
    }
    return new Promise<void>((resolve) => {
      this.pendingActions.push({ type: "set", ref, obj, resolvers: [resolve] });
      if (!this.timerHandle)
        this.timerHandle = setTimeout(this.databaseUpdateThread, this.DATABASE_UPDATE_MS);
    });
  };

  public remove = async (ref: DatabaseReference): Promise<void> => {
    Emitter.emit(DATABASE_SAVE_PENDING);
    if (this.offlineMode) {
      const filePath = (ref as any)._path.toString();
      deleteFilePath(filePath);
      return;
    }
    return new Promise<void>((resolve) => {
      this.pendingActions.push({ type: "remove", ref, resolvers: [resolve] });
      if (!this.timerHandle)
        this.timerHandle = setTimeout(this.databaseUpdateThread, this.DATABASE_UPDATE_MS);
    });
  };

  public update = async (
    updates: Record<string, unknown>,
    bypassIntegrity?: boolean  // retained for API compatibility; integrity check runs in databaseUpdateThread
  ): Promise<void> => {
    Emitter.emit(DATABASE_SAVE_PENDING);
    if (this.offlineMode) {
      for (const [key, value] of Object.entries(updates)) {
        await writeJsonFile(key, value);
      }
      return;
    }
    return new Promise<void>((resolve) => {
      const last = this.pendingActions[this.pendingActions.length - 1];
      if (last?.type === "update") {
        // coalesce into the queued update while preventing ancestor/descendant Firebase path collisions
        last.obj = Database.mergeQueuedUpdateObjects(last.obj ?? {}, updates);
        last.resolvers.push(resolve);
      } else {
        this.pendingActions.push({
          type: "update",
          ref: ref(this.database),
          obj: updates,
          resolvers: [resolve],
        });
      }
      if (!this.timerHandle)
        this.timerHandle = setTimeout(this.databaseUpdateThread, this.DATABASE_UPDATE_MS);
    });
  };

  public getUsers = async (): Promise<Record<string, IControl>> => {
    if (this.userCache) return this.userCache;
    await this.getTableSnap<Record<string, IControl>>("control").then((res) => {
      this.userCache = res;
    });
    return this.userCache ?? {};
  };

  // return any this.database record for the user id with corresponding token and user id to obtain settings
  public lookupUserByUsername = async (
    username: string
  ): Promise<IControl | undefined> => {
    const users = await this.getUsers();
    const id = Object.keys(users).find(
      (k) => users[k].username?.toLowerCase() === username?.toLowerCase()
    );
    if (!id) return undefined;
    return users[id];
  };

  private static readonly SYSTEM_USER_ID_CACHE_KEY = '__tarlings_system_userid';

  // return any this.database record for the user id with corresponding token and user id to obtain settings
  public lookupSystemUser = async (): Promise<IControl> => {
    if (this.systemUser) {
      loadPerf(`Database.lookupSystemUser resolved (memory cache hit)`);
      return this.systemUser;
    }
    // Persist the system user ID in localStorage — it is a fixed UUID that never changes,
    // so subsequent cold starts can skip the full users-table fetch entirely.
    const cachedId = typeof localStorage !== 'undefined'
      ? localStorage.getItem(Database.SYSTEM_USER_ID_CACHE_KEY)
      : null;
    if (cachedId) {
      loadPerf(`Database.lookupSystemUser resolved (localStorage cache hit)`, { systemUserId: cachedId });
      const control = { userid: cachedId, username: Database.systemUserName } as IControl;
      this.systemUser = control;
      return control;
    }
    loadPerf(`Database.lookupSystemUser fetching users table`);
    const users = await this.getUsers();
    const defaultControl = { userid: "", username: "" } as IControl;
    if (!users) return defaultControl;
    const id = Object.keys(users).find(
      (k) => users[k].username === Database.systemUserName
    );
    if (!id) return defaultControl;
    this.systemUser = users[id];
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(Database.SYSTEM_USER_ID_CACHE_KEY, id);
    }
    loadPerf(`Database.lookupSystemUser resolved (fetched + cached)`, { systemUserId: id });
    return users[id];
  };

  public snapAndSubSettings = (
    userid: string,
    cb: (settings: ISessionSettings) => void,
    options?: { disableLegacyFallback?: boolean; readinessTimeoutMs?: number }
  ): Unsubscribe => {
    const disableLegacyFallback = options?.disableLegacyFallback ?? false;
    // Template snapshots (system/owner profiles) have no custom schedule linkage to recover.
    const requireScheduleLinkage = !disableLegacyFallback;
    const readinessTimeoutMs = options?.readinessTimeoutMs ?? 1500;
    loadPerf(`Database.snapAndSubSettings subscribed`, { userid: userid.slice(0, 6) + '…', disableLegacyFallback });
    const _snapSettingsStart = performance.now(); // LOAD_PERF
    const subscriptionKeys = getStructuredSettingsSubscriptionKeys();
    const seenStructuredKeys = new Set<string>();
    let latestStructuredSnapshot: Record<string, unknown> | undefined;
    let latestLegacySnapshot: string | ISessionSettings | undefined;
    let legacyFetchStarted = false;
    let legacyFallbackResolved = false;
    let initialSettingsDelivered = false;
    let readinessTimeoutFired = false;
    const maybeMarkInitialDelivery = () => {
      if (initialSettingsDelivered) return;
      initialSettingsDelivered = true;
      loadPerf(`Database.snapAndSubSettings first delivery`, { userid: userid.slice(0, 6) + '…', elapsed: (performance.now() - _snapSettingsStart).toFixed(0) + 'ms', seenKeys: seenStructuredKeys.size, totalKeys: subscriptionKeys.length, hasStructured: !!latestStructuredSnapshot, legacyFallback: !!latestLegacySnapshot });
    };

    const emitSettings = () => {
      const legacyFallback = latestLegacySnapshot
        ? decodeSettingsSnapshot(latestLegacySnapshot, defaultSettings)
        : defaultSettings;

      const hasStructured = hasStructuredSettingsData(latestStructuredSnapshot);
      const structuredNeedsLegacyFallback =
        hasStructured &&
        structuredSnapshotNeedsLegacyFallback(
          latestStructuredSnapshot,
          requireScheduleLinkage
        );
      const hasAllStructuredKeys =
        seenStructuredKeys.size >= subscriptionKeys.length;
      const canUseStructuredByGate = shouldUseStructuredSettingsSnapshot(
        seenStructuredKeys.size,
        subscriptionKeys.length,
        latestStructuredSnapshot,
        !!latestLegacySnapshot,
        legacyFallbackResolved,
        requireScheduleLinkage
      );
      const canUseStructuredByV2AllKeys =
        disableLegacyFallback &&
        hasAllStructuredKeys &&
        hasStructured &&
        !structuredNeedsLegacyFallback;
      const canUseStructuredByTimeout =
        disableLegacyFallback &&
        readinessTimeoutFired &&
        hasStructured &&
        !structuredNeedsLegacyFallback;
      const canUseStructuredByInitialDelivery =
        disableLegacyFallback &&
        initialSettingsDelivered &&
        hasStructured &&
        !structuredNeedsLegacyFallback;

      if (
        canUseStructuredByGate ||
        canUseStructuredByV2AllKeys ||
        canUseStructuredByTimeout ||
        canUseStructuredByInitialDelivery
      ) {
        const resolvedSettings = decodeSettingsSnapshot(
          latestStructuredSnapshot,
          legacyFallback
        );
        cb(resolvedSettings);
        maybeMarkInitialDelivery();
        if (
          latestLegacySnapshot &&
          shouldBackfillStructuredSettings(
            latestStructuredSnapshot,
            latestLegacySnapshot
          )
        ) {
          void this.backfillStructuredSettings(userid, resolvedSettings);
        }
        return;
      }

      if (disableLegacyFallback) {
        const shouldSeedFromLegacy =
          (hasAllStructuredKeys || readinessTimeoutFired) &&
          (!hasStructured || structuredNeedsLegacyFallback);

        if (shouldSeedFromLegacy && !legacyFetchStarted) {
          void requestLegacyFallback(true);
        }

        if (legacyFallbackResolved) {
          const seededSettings = hasStructured
            ? decodeSettingsSnapshot(latestStructuredSnapshot, legacyFallback)
            : legacyFallback;
          cb(seededSettings);
          maybeMarkInitialDelivery();
          if (
            latestLegacySnapshot &&
            shouldBackfillStructuredSettings(
              latestStructuredSnapshot,
              latestLegacySnapshot
            )
          ) {
            void this.backfillStructuredSettings(userid, seededSettings);
          }
          return;
        }

        // Legacy seed request is in-flight; wait for it to resolve before delivering.
        if (shouldSeedFromLegacy && legacyFetchStarted) return;

        // All keys seen but no meaningful structured data and no legacy row exists.
        // Deliver defaults so awaitingSettings doesn't block forever.
        if (hasAllStructuredKeys) {
          cb(defaultSettings);
          maybeMarkInitialDelivery();
        }
        return;
      }

      if (latestLegacySnapshot || legacyFallbackResolved) {
        cb(legacyFallback);
        maybeMarkInitialDelivery();
        if (
          latestLegacySnapshot &&
          shouldBackfillStructuredSettings(
            latestStructuredSnapshot,
            latestLegacySnapshot
          )
        ) {
          void this.backfillStructuredSettings(userid, legacyFallback);
        }
      }
    };

    const requestLegacyFallback = async (force = false) => {
      if (
        !force && !shouldRequestLegacySettingsFallback(
          seenStructuredKeys.size,
          subscriptionKeys.length,
          latestStructuredSnapshot,
          legacyFetchStarted,
          requireScheduleLinkage
        )
      )
        return;

      if (legacyFetchStarted) return;
      legacyFetchStarted = true;
      latestLegacySnapshot = await this.getTableSnap<string | ISessionSettings>(
        "/settings/" + userid,
        false
      );
      legacyFallbackResolved = true;
      emitSettings();
    };

    const readinessTimer = setTimeout(() => {
      readinessTimeoutFired = true;
      emitSettings();
    }, readinessTimeoutMs);

    const structuredUnsubscribers = subscriptionKeys.map((key) => {
      const structuredRef = ref(this.database, `/settingsV2/${userid}/${key}`);
      return this.on(structuredRef, (value) => {
        if (value === this.pendingResponse) return;
        seenStructuredKeys.add(key);
        latestStructuredSnapshot = applyStructuredSubscriptionPatch(
          latestStructuredSnapshot,
          key,
          value
        );
        emitSettings();
        void requestLegacyFallback();
      });
    });

    return () => {
      clearTimeout(readinessTimer);
      structuredUnsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  };

  // returns the settings for the userid specified - recursively merges the owner and the system settings to a single set combined with overrides applied.
  public getUserSettings = async (
    userid: string,
    bypassCache?: boolean,
    // Owner-profile records are templates, so they carry no schedule linkage to recover.
    requireScheduleLinkage = !userid.endsWith(memberOwnerKeySuffix)
  ): Promise<ISessionSettings | undefined> => {
    const structuredSettings = await this.getTableSnap<unknown>(
      "/settingsV2/" + userid,
      bypassCache
    );

    let settings: ISessionSettings | undefined;
    if (hasStructuredSettingsData(structuredSettings)) {
      if (structuredSnapshotNeedsLegacyFallback(structuredSettings, requireScheduleLinkage)) {
        const settingsSnapshot = await this.getTableSnap<string | ISessionSettings>(
          "/settings/" + userid,
          bypassCache
        );
        const legacyFallback = settingsSnapshot
          ? decodeSettingsSnapshot(settingsSnapshot, defaultSettings)
          : defaultSettings;
        settings = decodeSettingsSnapshot(structuredSettings, legacyFallback);
        if (shouldBackfillStructuredSettings(structuredSettings, settingsSnapshot, requireScheduleLinkage)) {
          void this.backfillStructuredSettings(userid, settings);
        }
      } else {
        settings = decodeSettingsSnapshot(structuredSettings, defaultSettings);
      }
    } else {
      const settingsSnapshot = await this.getTableSnap<string | ISessionSettings>(
        "/settings/" + userid,
        bypassCache
      );
      if (!settingsSnapshot) return undefined;
      settings = decodeSettingsSnapshot(settingsSnapshot, defaultSettings);
      if (shouldBackfillStructuredSettings(structuredSettings, settingsSnapshot)) {
        void this.backfillStructuredSettings(userid, settings);
      }
    }

    if (!settings || Object.keys(settings).length === 0) {
      Emitter.emitNotification({
        message: "unable to fetch settings for user",
        error: true,
        fatal: true,
        notificationType: NotificationType.ErrorInfo,
      });
      throw new Error("Unable to fetch settings for user!");
    }
    settings = Database.migrateSettings(userid, settings, this);
    return settings;
  };

  // returns the system settings for new users to be cloned from
  public getSystemSettings = async (): Promise<ISessionSettings> => {
    if (this.systemSettings) return this.systemSettings;
    const systemId = await this.lookupSystemUser();
    if (!systemId) return defaultSettings;

    const structuredSettings = await this.getTableSnap<unknown>(
      "/settingsV2/" + systemId.userid
    );
    if (hasStructuredSettingsData(structuredSettings)) {
      // System settings are a template, so schedule linkage never applies.
      if (structuredSnapshotNeedsLegacyFallback(structuredSettings, false)) {
        const settingsSnapshot = await this.getTableSnap<string | ISessionSettings>(
          "/settings/" + systemId.userid
        );
        const legacyFallback = settingsSnapshot
          ? decodeSettingsSnapshot(settingsSnapshot, defaultSettings)
          : defaultSettings;
        this.systemSettings = decodeSettingsSnapshot(structuredSettings, legacyFallback);
        if (shouldBackfillStructuredSettings(structuredSettings, settingsSnapshot, false)) {
          void this.backfillStructuredSettings(systemId.userid, this.systemSettings);
        }
      } else {
        this.systemSettings = decodeSettingsSnapshot(
          structuredSettings,
          defaultSettings
        );
      }
      return this.systemSettings;
    }

    const settingsSnapshot = await this.getTableSnap<string | ISessionSettings>(
      "/settings/" + systemId.userid
    );
    this.systemSettings = decodeSettingsSnapshot(settingsSnapshot, defaultSettings);
    if (shouldBackfillStructuredSettings(structuredSettings, settingsSnapshot)) {
      void this.backfillStructuredSettings(systemId.userid, this.systemSettings);
    }
    return this.systemSettings;
  };

  // ensures a user doesnt already exist and creates a record for the token, username and id tuple and settings
  public createUser = async (
    userid: string,
    username: string,
    settings: ISessionSettings
  ): Promise<IControl | undefined> => {
    if (!userid || !username) {
      return undefined;
    }
    const match = await this.lookupUserByUsername(username);
    if (match) return match;

    setNewItemsToHandled(settings);
    stripSettings(
      settings,
      undefined,
      this.systemSettings,
      false,
      userid,
      this
    );

    const control = {
      userid,
      username,
      lastUpdateTime: this.getTimestamp() as Date,
      bank: 0,
      isAdmin: false,
      heartbeat: "newUser",
      created: this.getTimestamp() as Date,
    } as ControlRecord;

    const ownership = {
      userid: control.userid,
      username: control.username,
      loanedOwner: control.loanedOwner ?? null,
      loanedOwnerid: control.loanedOwnerid ?? null,
      otherOwnerIds: control.otherOwnerIds ?? null,
      owner: control.owner ?? null,
      ownerid: control.ownerid ?? null,
      virtualProfileId: control.virtualProfileId ?? null,
      virtualProfileImage: control.virtualProfileImage ?? null,
      virtualProfileName: control.virtualProfileName ?? null,
    } as IOwnershipControl;

    const userState = {
      audit: {},
      state: defaultState,
      ownedItems: [],
      sentMessages: {},
      sessionProgressState: {
        bank: control.bank ?? 0,
        penaltyPoints: control.penaltyPoints ?? 0,
        lastPenalties: control.lastPenalties ?? 0,
        lastScore: control.lastScore ?? 0,
        totalPenalties: control.totalPenalties ?? 0,
        totalScore: control.totalScore ?? 0,
      },
    };

    // purposefully not using our queue up mech - needs to complete before we continue signup
    await set(ref(this.database, "users/" + userid), userState);
    await set(
      ref(this.database, "settingsV2/" + userid),
      buildStructuredSettingsSnapshot(
        Database.migrateSettings(
          userid,
          cloneObject(settings) as ISessionSettings,
          this
        )
      )
    );
    await set(ref(this.database, "control/" + userid), control);
    await set(ref(this.database, "ownershipControl/" + userid), ownership);

    return control;
  };

  private getStructuredSnapshotFromCache = (userid: string): Record<string, unknown> | undefined => {
    const basePath = ref(this.database, `/settingsV2/${userid}`).toString();
    const subscriptionKeys = getStructuredSettingsSubscriptionKeys();
    const snapshot: Record<string, unknown> = {};
    let anyFound = false;
    for (const key of subscriptionKeys) {
      const subPath = `${basePath}/${key}`;
      if (
        Object.prototype.hasOwnProperty.call(this.valuesCache, subPath) &&
        this.valuesCache[subPath] !== this.pendingResponse
      ) {
        const value = this.valuesCache[subPath];
        if (value !== null && value !== undefined) snapshot[key] = value;
        anyFound = true;
      }
    }
    return anyFound ? snapshot : undefined;
  };

  // updates the settings and stamps the user as the owner to stop updates from settings member if owner has access
  // eslint-disable-next-line require-await
  public updateSettings = async (
    memberKey: string,
    settings: ISessionSettings,
    ownSettingsChange: boolean,
    changedItem?: ISettingsItem
  ): Promise<string | void> => {
    if (!memberKey) return;
    const updates: Record<string, unknown> = {};
    settings.userid = memberKey;
    stripImagesFromSettings(settings, memberKey, this);
    const migratedSettings = Database.migrateSettings(
      memberKey,
      cloneObject(settings) as ISessionSettings,
      this
    );
    const existingStructuredSettings =
      this.getStructuredSnapshotFromCache(memberKey) ??
      await this.getTableSnap<unknown>("/settingsV2/" + memberKey);
    Object.assign(
      updates,
      buildStructuredSettingsUpdates(
        memberKey,
        migratedSettings,
        existingStructuredSettings
      )
    );
    if (!ownSettingsChange) {
      const encryptItems = await this.getTableSnap<string>(
        "/users/" + memberKey + "/pendingState/",
        true
      );
      const items: IPendingItem[] = decrypt(encryptItems, []);
      if (changedItem) {
        // Targeted item update: replace any existing settingsItemUpdate for this key,
        // or append a new one. Avoids triggering a full settings re-fetch on the member.
        const existingIdx = items.findIndex(
          (p) => p.updateType === PendingUpdateType.settingsItemUpdate && p.item?.key === changedItem.key
        );
        const pendingEntry: IPendingItem = { updateType: PendingUpdateType.settingsItemUpdate, item: changedItem };
        if (existingIdx > -1) items[existingIdx] = pendingEntry;
        else items.push(pendingEntry);
      } else {
        // General settings change (not a single item) — use coarse refresh signal.
        if (items.findIndex((p) => p.updateType === PendingUpdateType.refreshSettings) === -1) {
          items.push({ updateType: PendingUpdateType.refreshSettings } as IPendingItem);
        }
      }
      const tempState = encrypt(items);
      await this.updatePendingState(memberKey, tempState);
    }
    return this.update(updates);
  };

  private getSettingsCollectionByType = (
    settings: ISessionSettings,
    itemType: ItemTypes
  ): (ISettingsItem & ICategoryItem)[] => {
    switch (itemType) {
      case ItemTypes.task:
        return (settings.creditSettings?.credits ?? []) as (ISettingsItem & ICategoryItem)[];
      case ItemTypes.penalty:
        return (settings.penaltySettings?.penalties ?? []) as (ISettingsItem & ICategoryItem)[];
      case ItemTypes.permission:
        return (settings.permissionSettings?.permissions ?? []) as (ISettingsItem & ICategoryItem)[];
      case ItemTypes.event:
        return (settings.eventsSettings?.events ?? []) as (ISettingsItem & ICategoryItem)[];
      case ItemTypes.position:
        return (settings.positionSettings?.positions ?? []) as (ISettingsItem & ICategoryItem)[];
      default:
        return [];
    }
  };

  public promoteSettingsItemToSystem = async (
    sourceUserId: string,
    item: ISettingsItem
  ): Promise<boolean> => {
    if (!sourceUserId || !item?.key || !item.itemType) return false;
    if (
      item.itemType === ItemTypes.event &&
      (item as any).eventType &&
      (item as any).eventType !== EventType.standard
    ) {
      return false;
    }

    const sourceSettings = await this.getUserSettings(sourceUserId, true);
    if (!sourceSettings) return false;

    const sourceCollection = this.getSettingsCollectionByType(
      sourceSettings,
      item.itemType
    );
    const sourceItem = sourceCollection.find((i) => i.key === item.key && !i.isDeleted);
    if (!sourceItem) return false;

    const systemUser = this.systemUser ?? await this.lookupSystemUser();
    if (!systemUser?.userid) return false;

    const systemSettings = cloneObject(await this.getSystemSettings()) as ISessionSettings;
    const systemCollection = this.getSettingsCollectionByType(
      systemSettings,
      item.itemType
    );
    if (systemCollection.find((i) => i.key === sourceItem.key)) return false;

    const promotedItem = cloneObject(sourceItem) as ISettingsItem & ICategoryItem;
    promotedItem.isNew = true;
    promotedItem.requiresValidation = true;
    promotedItem.createdDate = new Date();
    promotedItem.createdBy = sourceUserId;
    promotedItem.isCustom = false;
    promotedItem.isSystem = true;
    promotedItem.isOwner = false;
    promotedItem.isOverride = false;
    promotedItem.itemType = item.itemType;
    systemCollection.push(promotedItem);

    await this.updateSettings(systemUser.userid, systemSettings, true, promotedItem);
    return true;
  };

  // updates the settings and stamps the user as the owner to stop updates from settings member if owner has access
  public updateImageDb = async (
    imgId: string,
    img: string
  ): Promise<boolean> => {
    if (!imgId) return false;
    const updates: Record<string, unknown> = {};
    updates["/imageDb/" + imgId] = img;
    await this.update(updates);
    return true;
  };

  // updates the settings and stamps the user as the owner to stop updates from settings member if owner has access
  public updateBatchImageDb = async (
    updatedImages: { imgId: string; img: string }[]
  ): Promise<boolean> => {
    if (!updatedImages) return false;
    const updates: Record<string, unknown> = {};
    updatedImages.forEach((u) => {
      updates["/imageDb/" + u.imgId] = u.img;
    });
    await this.update(updates);
    return true;
  };

  // updates the settings and stamps the user as the owner to stop updates from settings member if owner has access
  public updatePendingState = async (
    userid: string,
    updatedState: string
  ): Promise<boolean> => {
    if (!userid) return false;
    const updates: Record<string, unknown> = {};
    updates["/users/" + userid + "/pendingState/"] = updatedState;
    await this.update(updates);
    return true;
  };

  public snapAndSubSessionIntegrity = (
    userid: string,
    cb: (id: string) => void
  ) => {
    const integrityRef = ref(
      this.database,
      "/users/" + userid + "/state/" + STATE_KEY_SESSION_ID
    );
    return this.on(integrityRef, cb);
  };

  // updates the settings and stamps the user as the owner to stop updates from settings member if owner has access
  public replaceUserState = async (
    userid: string,
    updatedState: Record<string, string>,
    sessionId: string,
    omitIntegrityChecks: boolean
  ): Promise<boolean> => {
    if (!userid) return false;

    if (this.isDisconnected)
      console.debug(
        "We are currently not connected so this request will block any updates from being queued so avoid this until we are connected"
      );
    // catch that we are actually updating the blinking session key here
    if (
      !this.isDisconnected &&
      !omitIntegrityChecks &&
      !updatedState[STATE_KEY_SESSION_ID]
    ) {
      // snapAndSubSessionIntegrity keeps this node live, so read through the cache
      // instead of issuing a fresh get on every single state push.
      const integrityRef = await this.getTableSnap<string>(
        "/users/" + userid + "/state/" + STATE_KEY_SESSION_ID
      );
      if (integrityRef !== sessionId) {
        console.error("Session integrity check failed - refusing to update state -  Did owner try to update member state?");
        return false;
      };
    }

    const updates: Record<string, unknown> = {};
    Object.keys(updatedState).forEach(
      (key) =>
        (updates["/users/" + userid + "/state/" + key] = updatedState[key])
    );
    await this.update(updates);
    return true;
  };

  // updates the settings and stamps the user as the owner to stop updates from settings member if owner has access
  public updateSessionKey = async (
    userid: string,
    sessionId: string
  ): Promise<boolean> => {
    if (!userid) return false;
    this.sessionId = sessionId;
    // catch that we are actually updating the blinking session key here
    const updates: Record<string, unknown> = {};
    updates["/users/" + userid + "/state/" + STATE_KEY_SESSION_ID] = sessionId;
    await update(ref(this.database), updates);
    return true;
  };

  public snapControlRecord = async (userid: string): Promise<IControl> => {
    loadPerf(`Database.snapControlRecord called`);
    if (this.allControlRecords) {
      const match = this.allControlRecords[userid];
      if (match) {
        loadPerf(`Database.snapControlRecord resolved (cache hit)`);
        return match;
      }
    }
    const result = await this.getTableSnap<IControl>("control/" + userid);
    loadPerf(`Database.snapControlRecord resolved (db fetch)`);
    return result;
  };

  // we can limit downloads by pulling out those elements which are editable outside of sessions from the state object
  // returns a list of audit records for a given user and realtime updates
  public snapState = async (
    userid: string
  ): Promise<Record<string, string>> => {
    loadPerf(`Database.snapState called`);
    const result = await this.getTableSnap<Record<string, string>>(
      "users/" + userid + "/state"
    );
    loadPerf(`Database.snapState resolved`, { bytes: JSON.stringify(result ?? {}).length });
    return result;
  };

  /**
   * more expensive with callbacks on changes but owners need this to know when things have started
   * @param userid
   * @param stateUpdated
   */
  public snapAndSubState = (
    userid: string,
    stateUpdated: (result: any) => void
  ): Unsubscribe => {
    loadPerf(`Database.snapAndSubState subscribed`);
    let _stateFirstFire = true;
    const stateRef = ref(this.database, "users/" + userid + "/state");
    return this.on(stateRef, (snapshot: any) => {
      if (_stateFirstFire) {
        _stateFirstFire = false;
        loadPerf(`Database.snapAndSubState first callback`, { bytes: JSON.stringify(snapshot ?? {}).length });
      }
      stateUpdated(snapshot);
    });
  };

  public snapAndSubPendingState = (
    userid: string,
    stateUpdated: (snapshot: any) => void
  ): Unsubscribe => {
    loadPerf(`Database.snapAndSubPendingState subscribed`);
    let _pendingFirstFire = true;
    const stateRef = ref(this.database, "users/" + userid + "/pendingState");
    return this.on(stateRef, (snapshot: any) => {
      if (_pendingFirstFire) {
        _pendingFirstFire = false;
        loadPerf(`Database.snapAndSubPendingState first callback`, { count: Array.isArray(snapshot) ? snapshot.length : Object.keys(snapshot ?? {}).length });
      }
      stateUpdated(snapshot);
    });
  };

  public snapAndSubMemberMessages = (
    userid: string,
    memberMessagesUpdated: (snapshot: any) => void,
    memberMessagesNotification: (snapshot: any) => void
  ): Promise<() => void> => {
    // Use a persistent live subscription on /memberMessages so that every
    // change (new message, delete, read-status update) calls memberMessagesUpdated
    // and replaces the cache array.  This ensures React sees a new reference and
    // re-renders even when only a push/splice was done locally.
    const messagesRef = ref(this.database, "users/" + userid + "/memberMessages");
    const messagesUnsub = this.on(messagesRef, memberMessagesUpdated);

    // delete any stale notification entries, then subscribe to new ones
    return this.remove(
      ref(this.database, "users/" + userid + "/memberMessagesNotifications")
    ).then(() => {
      const stateRef = ref(
        this.database,
        "users/" + userid + "/memberMessagesNotifications"
      );
      const notifUnsub = this.on(stateRef, memberMessagesNotification);
      return () => {
        messagesUnsub();
        notifUnsub();
      };
    });
  };

  public snapAndSubSentMessages = (
    userid: string,
    onSentMessagesChanged: (
      sentReceipts: Record<string, IMemberSentReceipt>
    ) => void
  ): Unsubscribe => {
    const stateRef = ref(this.database, "users/" + userid + "/sentMessages");
    return this.on(stateRef, onSentMessagesChanged);
  };

  public snapAndSubLovenseToys = (
    userid: string,
    toysUpdated: (snapshot: any) => void
  ): Unsubscribe => {
    const stateRef = ref(this.database, "users/" + userid + "/toys");
    return this.on(stateRef, toysUpdated);
  };

  public snapAndSubOwnedItems = (
    userid: string,
    ownedItemsUpdated: (snapshot: any) => void
  ): Unsubscribe => {
    loadPerf(`Database.snapAndSubOwnedItems subscribed`);
    let _ownedFirstFire = true;
    const stateRef = ref(this.database, "users/" + userid + "/ownedItems");
    return this.on(stateRef, (snapshot: any) => {
      if (_ownedFirstFire) {
        _ownedFirstFire = false;
        loadPerf(`Database.snapAndSubOwnedItems first callback`, { bytes: JSON.stringify(snapshot ?? {}).length, keys: Object.keys(snapshot ?? {}).length });
      }
      ownedItemsUpdated(snapshot);
    });
  };

  private allControlCallbacks: ((snapshot: ControlRecord) => void)[] = [];
  private allControlRecords: Record<string, ControlRecord> = {};
  private allControlRaw: Record<string, string> = {};
  private allControlSubscription: Unsubscribe | undefined;
  // returns a set if owned sessions and a subscription for changes
  public snapAndSubAllControl = (
    controlUpdated: (snapshot: ControlRecord) => void
  ): void => {
    this.allControlCallbacks.push(controlUpdated);
    if (
      this.allControlRecords &&
      Object.keys(this.allControlRecords).length > 0
    ) {
      Object.values(this.allControlRecords).forEach((control) =>
        controlUpdated(control)
      );
    }
    if (this.allControlSubscription) return;
    // One subscription on /control rather than a full table read plus a listener
    // per user, which downloaded the whole table twice on every session start.
    this.allControlSubscription = this.on(
      ref(this.database, "control"),
      (vals: Record<string, IControl>) => {
        if (!vals || vals === (this.pendingResponse as unknown)) return;
        Object.keys(vals).forEach((key) => {
          const record = vals[key];
          if (!record) return;
          const raw = JSON.stringify(record);
          if (this.allControlRaw[key] === raw) return;
          this.allControlRaw[key] = raw;
          this.handleControlRecordSubscription(record);
        });
      }
    );
  };

  private handleControlRecordSubscription = (c: IControl) => {
    if (!c.userid) return;
    Database.decryptControlRecord(c);
    this.allControlRecords[c.userid] = new ControlRecord(c);
    this.allControlCallbacks.forEach((cb) =>
      cb(this.allControlRecords[c.userid])
    );
  };

  // returns a list of audit records for a given user and realtime updates
  public snapAndSubAuditLogs = (
    userid: string,
    callback: (snapshot: any) => void
  ): Unsubscribe => {
    if (
      !userid &&
      (!this.systemUser?.userid ||
        this.userRecord?.userid !== this.systemUser.userid)
    )
      return () => { }; // root subscription pulls every user's logs
    const auditRef = ref(this.database, "/audit/logs/" + userid);
    return this.on(auditRef, callback);
  };
  // returns a list of audit records for a given user and realtime updates
  public snapAndSubAuditBank = (
    userid: string,
    callback: (snapshot: any) => void
  ): Unsubscribe => {
    const auditRef = ref(this.database, "/audit/bank/" + userid);
    return this.on(auditRef, callback);
  };
  // returns a list of audit records for a given user and realtime updates
  public snapAndSubAuditScores = (
    userid: string,
    callback: (snapshot: any) => void
  ): Unsubscribe => {
    const auditRef = ref(this.database, "/audit/scores/" + userid);
    return this.on(auditRef, callback);
  };
  // returns a list of audit records for a given user and realtime updates
  public snapAuditScores = (userid: string): Promise<IAuditScoreHistory[]> => {
    return this.getTableSnap<IAuditScoreHistory[]>("/audit/scores/" + userid);
  };
  // returns a list of task locks for a given user with a listener to find new ones as added
  public snapAndSubTaskLocks = (
    userid: string,
    callback: (snapshot: any, userid: string) => void
  ): Unsubscribe => {
    const path = "/locks/" + userid;
    const locksRef = ref(this.database, path);
    return this.on(locksRef, (val) => callback(val, userid));
  };
  // returns a list of task locks for a given user with a listener to find new ones as added
  public snapAndSubProofRequests = (
    userid: string,
    callback: (snapshot: any) => void
  ): Unsubscribe => {
    const path = "/proofRequests/" + userid;
    const proofRef = ref(this.database, path);
    return this.on(proofRef, callback);
  };

  public snapAndSubWeeklyTargetProgress = (
    userid: string,
    callback: (snapshot: Record<string, Record<string, IWeeklyTargetProgress>>) => void
  ): Unsubscribe => {
    const path = `/${WeeklyTargetProgressPath}/${userid}`;
    const weeklyTargetRef = ref(this.database, path);
    return this.on(weeklyTargetRef, (snapshot) => {
      callback((snapshot as Record<string, Record<string, IWeeklyTargetProgress>>) || {});
    });
  };

  public saveWeeklyTargetProgress = async (
    userid: string,
    progress: IWeeklyTargetProgress
  ): Promise<void> => {
    if (!userid || !progress?.taskKey || !progress?.weekKey) return;
    const updates: Record<string, unknown> = {};
    updates[`/${WeeklyTargetProgressPath}/${userid}/${progress.taskKey}/${progress.weekKey}`] = progress;
    await this.update(updates);
  };

  public setWeeklyTargetNextValue = async (
    userid: string,
    taskKey: string,
    weekKey: string,
    nextWeekTargetValue: number
  ): Promise<void> => {
    if (!userid || !taskKey || !weekKey) return;
    const updates: Record<string, unknown> = {};
    updates[`/${WeeklyTargetProgressPath}/${userid}/${taskKey}/${weekKey}/nextWeekTargetValue`] = nextWeekTargetValue;
    await this.update(updates);
  };

  public saveDailyScore = async (
    userid: string,
    score: number,
    bank: number,
    penalties: number
  ): Promise<void> => {
    if (!userid) return;
    await this.getTableSnap<IAuditScoreHistory[]>(
      "/audit/scores/" + userid
    ).then(async (history) => {
      const updates: Record<string, unknown> = {};

      let newAudit: IAuditScoreHistory[] = [];

      if (history) newAudit = history;

      while (newAudit.length > 6) newAudit.splice(0, 1);

      newAudit.push({ date: new Date().toISOString(), penalties, bank, score });

      updates["/audit/scores/" + userid] = newAudit;

      await this.update(updates);
    });
  };

  public rollAudit = async (userid: string): Promise<void> => {
    if (!userid) return;
    if (
      this.userRecord &&
      this.userRecord.lastUpdateTime.getDate() === new Date().getDate()
    )
      return;
    const historyLength = 7;
    // Compute the set of valid day-of-month keys (today and prior historyLength-1 days),
    // handling month wrap-around via setDate arithmetic.
    const validDays = new Set<number>();
    for (let i = 0; i < historyLength; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      validDays.add(d.getDate());
    }
    // Null-out all stale day-of-month keys without reading the audit data.
    // Firebase update ignores nulls for paths that don't exist, so this is safe.
    const updates: Record<string, unknown> = {};
    for (const collection of ["logs", "bank"]) {
      for (let day = 1; day <= 31; day++) {
        if (!validDays.has(day)) {
          updates[`/audit/${collection}/${userid}/${day}`] = null;
        }
      }
    }
    if (Object.keys(updates).length > 0) await this.update(updates);
  };

  public auditBankChange(bank: number, reason: string, userid: string): void {
    const date = getDate(new Date());
    const dbRef = ref(this.database, `/audit/bank/${userid}/${date}`);
    const newChildRef = push(dbRef);
    void this.set(newChildRef, {
      time: getTime(new Date()),
      bank,
      reason: encrypt(reason),
    });
  }

  // audits the user action
  public auditUser = async (
    auditMessages: AuditMessage[],
    logUser: IUserRecord
  ): Promise<void> => {
    if (!logUser || !logUser.userid) {
      return;
    }

    const auditItems = auditMessages.map((auditItem) => {
      auditItem.username = logUser.username;
      auditItem.userid = logUser.userid;
      if (auditItem.userid.endsWith(memberOwnerKeySuffix))
        auditItem.userid = auditItem.userid.replace(memberOwnerKeySuffix, "");
      return auditItem;
    });

    const updates: Record<string, unknown> = {};
    this.collectLogUpdates(updates, auditItems, logUser);

    if (this.userRecord && this.userRecord?.userid !== logUser.userid) {
      this.collectLogUpdates(
        updates,
        auditMessages.map(
          (auditMessage) =>
            new AuditMessage(
              auditMessage.type,
              auditMessage.name,
              `${logUser.username} - ${auditMessage.action}`,
              auditMessage.level,
              auditMessage.success,
              auditMessage.cost
            )
        ),
        this.userRecord as IControl
      );
    }

    if (Object.keys(updates).length === 0) return;
    await this.update(updates);
  };

  // One multi-path update rather than a push+set round trip per audit line.
  private collectLogUpdates = (
    updates: Record<string, unknown>,
    auditItems: AuditMessage[],
    logUser: IUserRecord
  ): void => {
    const date = getDate(new Date());
    const nodePath = `/audit/logs/${logUser.userid}/${date}`;
    const dbRef = ref(this.database, nodePath);
    auditItems.forEach((auditItem) => {
      const key = push(dbRef).key;
      if (!key) return;
      updates[`${nodePath}/${key}`] = encrypt({
        ...auditItem,
        userid: logUser.userid,
        username: logUser.username || null,
        success: auditItem.success || null,
        cost: auditItem.cost || null,
      });
    });
  };

  // takes ownership of a user id for a give owner id, stamping the owner on the user and updates the owner to show user is owned and not shared anymore
  // eslint-disable-next-line require-await
  public takeOwnership = async (
    userid: string,
    username: string,
    ownerId: string,
    ownerName: string,
    isLoan: boolean
  ): Promise<string | void> => {
    const updates: Record<string, unknown> = {};
    if (!isLoan) {
      updates["/control/" + userid + "/owner"] = ownerName;
      updates["/control/" + userid + "/ownerid"] = ownerId;
      void this.remove(
        ref(this.database, "/control/" + userid + "/loanedOwner")
      );
      void this.remove(
        ref(this.database, "/control/" + userid + "/loanedOwnerid")
      );
      void this.remove(
        ref(this.database, "/control/" + userid + "/otherOwnerIds")
      );
      updates["/ownershipControl/" + userid + "/owner"] = ownerName;
      updates["/ownershipControl/" + userid + "/ownerid"] = ownerId;
      void this.remove(
        ref(this.database, "/ownershipControl/" + userid + "/loanedOwner")
      );
      void this.remove(
        ref(this.database, "/ownershipControl/" + userid + "/loanedOwnerid")
      );
      void this.remove(
        ref(this.database, "/ownershipControl/" + userid + "/otherOwnerIds")
      );
    } else {
      updates["/control/" + userid + "/loanedOwner"] = ownerName;
      updates["/control/" + userid + "/loanedOwnerid"] = ownerId;
      updates["/ownershipControl/" + userid + "/loanedOwner"] = ownerName;
      updates["/ownershipControl/" + userid + "/loanedOwnerid"] = ownerId;
    }

    //clear any virtual profile now we are owned
    void this.remove(
      ref(this.database, "/control/" + userid + "/virtualProfileId")
    );
    void this.remove(
      ref(this.database, "/control/" + userid + "/virtualProfileName")
    );
    void this.remove(
      ref(this.database, "/control/" + userid + "/virtualProfileImage")
    );
    void this.remove(
      ref(this.database, "/ownershipControl/" + userid + "/virtualProfileId")
    );
    void this.remove(
      ref(this.database, "/ownershipControl/" + userid + "/virtualProfileName")
    );
    void this.remove(
      ref(this.database, "/ownershipControl/" + userid + "/virtualProfileImage")
    );

    const dbRef = ref(this.database, "/audit/owners/" + userid);
    const newChildRef = push(dbRef);
    void this.set(
      newChildRef,
      encrypt({
        date: new Date(),
        ownerName,
        type: isLoan ? "Loan" : "Owned",
      })
    );

    await this.update(updates);
  };

  // takes ownership of a user id for a give owner id, stamping the owner on the user and updates the owner to show user is owned and not shared anymore
  // eslint-disable-next-line require-await
  public updateOtherOwners = async (
    userid: string,
    otherOwnerIds: string[],
    additions?: string[],
    removes?: string[]
  ): Promise<string | void> => {
    const updates: Record<string, unknown> = {};
    updates["/control/" + userid + "/otherOwnerIds"] = otherOwnerIds;
    updates["/ownershipControl/" + userid + "/otherOwnerIds"] = otherOwnerIds;

    const dbRef = ref(this.database, "/audit/owners/" + userid);
    const newChildRef = push(dbRef);
    if (additions) {
      additions.forEach(
        (newSupervisor) =>
          void this.set(
            newChildRef,
            encrypt({
              date: new Date(),
              ownerName: newSupervisor,
              type: "Added: Supervisor",
            })
          )
      );
    }
    if (removes) {
      removes.forEach(
        (oldSupervisor) =>
          void this.set(
            newChildRef,
            encrypt({
              date: new Date(),
              ownerName: oldSupervisor,
              type: "Removed: Supervisor",
            })
          )
      );
    }

    await this.update(updates);
  };

  // takes ownership of a user id for a give owner id, stamping the owner on the user and updates the owner to show user is owned and not shared anymore
  public takeVirtualOwnership = async (
    userid: string,
    profileid: string,
    profileName: string,
    profileImage: string
  ): Promise<string | void> => {
    const updates: Record<string, unknown> = {};
    updates["/control/" + userid + "/virtualProfileId"] = profileid;
    updates["/control/" + userid + "/virtualProfileName"] = profileName ?? null;
    updates["/control/" + userid + "/virtualProfileImage"] =
      profileImage ?? null;
    updates["/ownershipControl/" + userid + "/virtualProfileId"] = profileid;
    updates["/ownershipControl/" + userid + "/virtualProfileName"] =
      profileName ?? null;
    updates["/ownershipControl/" + userid + "/virtualProfileImage"] =
      profileImage ?? null;
    const dbRef = ref(this.database, "/audit/owners/" + userid);
    const newChildRef = push(dbRef);
    void this.set(
      newChildRef,
      encrypt({
        date: new Date(),
        ownerName: profileName ?? null,
        type: "Virtual",
      })
    );
    await this.update(updates);
  };

  public getOwnerHistory = async (userid: string): Promise<IOwnerHistory[]> => {
    const results = await this.getTableSnap<Record<string, string>>(
      `/audit/owners/${userid}`
    );
    if (!results) return [];
    return Object.values(results).map((p) =>
      decrypt<IOwnerHistory>(p, {} as IOwnerHistory)
    );
  };

  // removes ownership of a user leaving user unowned and able to update their own state
  public deleteUser = async (deleteUserId: string): Promise<void> => {
    if (!deleteUserId) return;
    const users = await this.getUsers();
    const deleteUserRecord = users[deleteUserId];
    // delete the user
    void this.remove(ref(this.database, "/users/" + deleteUserId));
    void this.remove(ref(this.database, "/ownershipControl/" + deleteUserId));
    void this.remove(ref(this.database, "/control/" + deleteUserId));
    void this.remove(ref(this.database, "/voteTasks/" + deleteUserId));

    void this.remove(ref(this.database, "/settings/" + deleteUserId));
    void this.remove(ref(this.database, "/settingsV2/" + deleteUserId));
    void this.remove(ref(this.database, "/locks/" + deleteUserId));
    void this.remove(ref(this.database, "/proofRequests/" + deleteUserId));

    void this.remove(ref(this.database, `/audit/logs/${deleteUserId}`));
    void this.remove(ref(this.database, `/audit/scores/${deleteUserId}`));
    void this.remove(ref(this.database, `/audit/owners/${deleteUserId}`));
    void this.remove(ref(this.database, `/audit/bank/${deleteUserId}`));

    void this.deleteUserPillories(deleteUserId);

    if (!users) return;

    // remove any references to this user from other sessions
    Object.keys(users).forEach((userKey) => {
      if (userKey === deleteUserId) return;
      if (
        new ControlRecord(users[userKey]).getOwnerType() === OwnerType.unowned
      )
        return; // no point looking as this has no owner
      const profile = users[userKey];
      if (!profile) return;

      if (profile.ownerid === deleteUserId) {
        void this.remove(
          ref(this.database, "/control/" + userKey + "/owner")
        );
        void this.remove(
          ref(this.database, "/control/" + userKey + "/ownerid")
        );
        void this.remove(
          ref(this.database, "/ownershipControl/" + userKey + "/owner")
        );
        void this.remove(
          ref(this.database, "/ownershipControl/" + userKey + "/ownerid")
        );
      }

      if (profile.virtualProfileId === deleteUserId) {
        void this.remove(
          ref(this.database, "/control/" + userKey + "/virtualProfileImage")
        );
        void this.remove(
          ref(this.database, "/control/" + userKey + "/virtualProfileName")
        );
        void this.remove(
          ref(this.database, "/control/" + userKey + "/virtualProfileId")
        );
        void this.remove(
          ref(
            this.database,
            "/ownershipControl/" + userKey + "/virtualProfileImage"
          )
        );
        void this.remove(
          ref(
            this.database,
            "/ownershipControl/" + userKey + "/virtualProfileName"
          )
        );
        void this.remove(
          ref(
            this.database,
            "/ownershipControl/" + userKey + "/virtualProfileId"
          )
        );
      }

      if (profile.loanedOwnerid === deleteUserId) {
        void this.remove(
          ref(this.database, "/control/" + userKey + "/loanedOwner")
        );
        void this.remove(
          ref(this.database, "/control/" + userKey + "/loanedOwnerid")
        );
        void this.remove(
          ref(this.database, "/ownershipControl/" + userKey + "/loanedOwner")
        );
        void this.remove(
          ref(this.database, "/ownershipControl/" + userKey + "/loanedOwnerid")
        );
      }

      if (
        profile.otherOwnerIds &&
        profile.otherOwnerIds.includes(deleteUserId)
      ) {
        const updates: Record<string, unknown> = {};
        profile.otherOwnerIds.splice(
          profile.otherOwnerIds.indexOf(deleteUserId),
          1
        );
        updates["/control/" + userKey + "/otherOwnerIds"] =
          profile.otherOwnerIds;
        void this.update(updates);
      }
    });
  };

  // removes ownership of a user leaving user unowned and able to update their own state
  public removeOwnership = async (member: ControlRecord): Promise<void> => {
    if (member.getOwnerType() === OwnerType.owned) {
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/owner")
      );
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/ownerid")
      );
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/otherOwnerIds")
      );
      void this.remove(
        ref(this.database, "/ownershipControl/" + member.userid + "/owner")
      );
      void this.remove(
        ref(this.database, "/ownershipControl/" + member.userid + "/ownerid")
      );
      void this.remove(
        ref(
          this.database,
          "/ownershipControl/" + member.userid + "/otherOwnerIds"
        )
      );
    } else if (member.getOwnerType() === OwnerType.virtual) {
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/virtualProfileImage")
      );
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/virtualProfileName")
      );
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/virtualProfileId")
      );
      void this.remove(
        ref(
          this.database,
          "/ownershipControl/" + member.userid + "/virtualProfileImage"
        )
      );
      void this.remove(
        ref(
          this.database,
          "/ownershipControl/" + member.userid + "/virtualProfileName"
        )
      );
      void this.remove(
        ref(
          this.database,
          "/ownershipControl/" + member.userid + "/virtualProfileId"
        )
      );
    } else if (member.getOwnerType() === OwnerType.loaned) {
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/loanedOwner")
      );
      await this.remove(
        ref(this.database, "/control/" + member.userid + "/loanedOwnerid")
      );
      void this.remove(
        ref(
          this.database,
          "/ownershipControl/" + member.userid + "/loanedOwner"
        )
      );
      void this.remove(
        ref(
          this.database,
          "/ownershipControl/" + member.userid + "/loanedOwnerid"
        )
      );
    }
  };

  public deleteMessage = async (
    key: string,
    toUserid: string
  ): Promise<void> => {
    return await this.remove(
      ref(this.database, "/users/" + toUserid + "/memberMessages/" + key)
    );
  };

  public deleteSentReceipt = async (
    key: string,
    fromUserid: string
  ): Promise<void> => {
    return await this.remove(
      ref(this.database, "/users/" + fromUserid + "/sentMessages/" + key)
    );
  };

  public deleteOldVotes = async (): Promise<void> => {
    if (
      this.userRecord &&
      this.systemUser &&
      this.userRecord.userid === this.systemUser.userid &&
      differenceInDays(this.userRecord.lastUpdateTime, new Date()) === 0
    )
      return;

    const votes = await this.getTableSnap<
      Record<string, Record<string, string>>
    >("/voteTasks/");
    if (!votes) return;
    const finishedCutOffDate = subWeeks(new Date(), 4);
    Object.keys(votes).forEach((userKey) => {
      const userVotes = votes[userKey];
      Object.keys(userVotes).forEach((voteKey) => {
        const voteDetails = decrypt<IVoteTask>(
          userVotes[voteKey],
          {} as IVoteTask
        );
        const voteTask = new VoteTask(voteDetails);
        if (!isAfter(voteTask.getEndTime(), finishedCutOffDate)) {
          void this.remove(ref(this.database, "/voteTaskKeys/" + voteKey));
          void this.remove(
            ref(this.database, "/voteTasks/" + userKey + "/" + voteKey)
          );
        }
      });
    });
  };

  public deleteOldPillories = async (): Promise<void> => {
    if (
      this.userRecord &&
      this.systemUser &&
      this.userRecord.userid === this.systemUser.userid &&
      differenceInDays(this.userRecord.lastUpdateTime, new Date()) === 0
    )
      return;

    const pillories = await this.getTableSnap<Record<string, string>>(
      "/pillory/"
    );
    if (!pillories) return;
    const finishedCutOffDate = subWeeks(new Date(), 4);
    Object.keys(pillories).forEach((pilloryKey) => {
      const pilloryStr = pillories[pilloryKey];
      const pilloryDetails = decrypt<IPilloryTopic>(
        pilloryStr,
        {} as IPilloryTopic
      );
      const pilloryItem = new PilloryTopic(pilloryDetails);
      if (!isAfter(new Date(pilloryItem.pilloryEndTime), finishedCutOffDate)) {
        void this.remove(ref(this.database, "/pillory/" + pilloryKey));
      }
    });
  };

  /**
   * Removes stale lock receipts under /locks on system startup.
   * Deletes:
   *  - Any receipt with isComplete=true (task unlocked, member notified).
   *  - Any receipt older than 4 weeks (abandoned / never resolved).
   * Guarded by the same same-day check used by deleteOldVotes so it only
   * runs once per system-logon day.
   */
  public deleteOldLocks = async (): Promise<void> => {
    if (
      this.userRecord &&
      this.systemUser &&
      this.userRecord.userid === this.systemUser.userid &&
      differenceInDays(this.userRecord.lastUpdateTime, new Date()) === 0
    )
      return;

    const locks = await this.getTableSnap<Record<string, Record<string, ILockReceipt>>>("/locks/");
    if (!locks) return;

    const cutoffDate = subYears(new Date(), 1);
    Object.keys(locks).forEach((userid) => {
      const userLocks = locks[userid];
      if (!userLocks) return;
      Object.keys(userLocks).forEach((imageKey) => {
        const receipt = userLocks[imageKey];
        if (!receipt) return;
        const isComplete = receipt.isComplete === true;
        const isAbandoned = receipt.startTime && isBefore(new Date(receipt.startTime), cutoffDate);
        if (isComplete || isAbandoned) {
          void this.remove(ref(this.database, `/locks/${userid}/${imageKey}`));
        }
      });
    });
  };

  public deleteUserPillories = async (userId: string): Promise<void> => {
    const pillories = await this.getTableSnap<Record<string, string>>(
      "/pillory/"
    );
    if (!pillories) return;
    Object.keys(pillories).forEach((pilloryKey) => {
      if (pilloryKey.startsWith(userId)) {
        void this.remove(ref(this.database, "/pillory/" + pilloryKey));
      }
    });
  };

  public deleteOldMessages = async (
    userid: string,
    mode: DeleteMessageMode
  ): Promise<string | void> => {
    if (
      this.userRecord &&
      mode === DeleteMessageMode.Housekeep &&
      differenceInDays(this.userRecord.lastUpdateTime, new Date()) === 0
    )
      return;
    const messages = await this.getTableSnap<Record<string, IMemberMessage>>(
      "/users/" + userid + "/memberMessages"
    );
    if (!messages) return;
    const newMessageStack = {
      ...messages,
    };
    const readCutOffDate = subWeeks(new Date(), 1);
    const unreadCutOffDate = subWeeks(new Date(), 4);
    Object.keys(messages).forEach((key) => {
      const { read, sent, keep } = messages[key];
      if (keep) return;
      if (
        mode === DeleteMessageMode.Housekeep &&
        ((!read && isAfter(new Date(sent), unreadCutOffDate)) ||
          (read && isAfter(new Date(sent), readCutOffDate)))
      )
        return;
      if (mode === DeleteMessageMode.Read && !read) return;
      delete newMessageStack[key];
    });
    const updates: Record<string, unknown> = {};
    updates["/users/" + userid + "/memberMessages"] = newMessageStack;
    return this.update(updates);
  };

  public deleteOldSentMessages = async (
    userId: string,
    mode: DeleteMessageMode
  ): Promise<string | void> => {
    if (
      this.userRecord &&
      mode === DeleteMessageMode.Housekeep &&
      differenceInDays(this.userRecord.lastUpdateTime, new Date()) === 0
    )
      return;
    const messages = await this.getTableSnap<
      Record<string, IMemberSentReceipt>
    >("/users/" + userId + "/sentMessages");
    if (!messages) return;
    const newMessageStack = {
      ...messages,
    };
    const cutOffDate = subWeeks(new Date(), 1);
    Object.keys(messages).forEach((key) => {
      const { read, sent } = messages[key];
      if (
        mode === DeleteMessageMode.Housekeep &&
        isAfter(new Date(sent), cutOffDate)
      )
        return;
      if (mode === DeleteMessageMode.Read && !read) return;
      delete newMessageStack[key];
    });
    const updates: Record<string, unknown> = {};
    updates["/users/" + userId + "/sentMessages"] = newMessageStack;
    return this.update(updates);
  };

  private pendingMessageReadUpdates: Record<string, unknown> = {};

  private flushMessageReadUpdates = debounce(() => {
    const updates = this.pendingMessageReadUpdates;
    this.pendingMessageReadUpdates = {};
    if (Object.keys(updates).length === 0) return;
    void this.update(updates);
  }, 500);

  public setMessageRead = async (
    key: string,
    toUserid: string,
    fromUserid: string
  ): Promise<void> => {
    this.pendingMessageReadUpdates[
      "/users/" + toUserid + "/memberMessages/" + key + "/read"
    ] = true;
    // Each receipt key is only ever read when marking that one message, so the cached
    // read is enough and avoids a forced round trip per message.
    const receipt = await this.getTableSnap<IMemberSentReceipt>(
      `/users/${fromUserid}/sentMessages/${key}`
    );
    if (receipt && !receipt.read) {
      this.pendingMessageReadUpdates[
        "/users/" + fromUserid + "/sentMessages/" + key + "/read"
      ] = true;
      this.pendingMessageReadUpdates[
        "/users/" + fromUserid + "/sentMessages/" + key + "/readDate"
      ] = this.getTimestamp();
    }
    this.flushMessageReadUpdates();
  };

  public setMessageKeepState = async (
    key: string,
    toUserid: string,
    keep: boolean
  ): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates["/users/" + toUserid + "/memberMessages/" + key + "/keep"] = keep;
    await this.update(updates);
  };

  private pushMessageToUser = async (
    recipientId: string,
    senderId: string,
    senderName: string,
    message: string,
    subject: string,
    toUsername: string,
    messageType: MessageType,
    state?: any
  ): Promise<string> => {
    const memberRef = ref(
      this.database,
      "/users/" + recipientId + "/memberMessages"
    );
    const newResponseRef = push(memberRef);
    const content: MessageContents = {
      message,
      subject,
      fromUserid: senderId,
      fromUsername: senderName,
      toUsername,
      messageType,
    };
    if (state) content.state = state;

    const messagePayload: unknown = {
      key: newResponseRef.key as string,
      message: encrypt(content),
      read: false,
      sent: this.getTimestamp(),
      keep: false,
    };

    await this.set(newResponseRef, messagePayload);
    const updates: Record<string, unknown> = {};
    updates[
      `/users/${recipientId}/memberMessagesNotifications/${newResponseRef.key}`
    ] = messagePayload;
    void this.update(updates);

    return newResponseRef.key as string;
  };

  private createSentReceipt = (
    messageKey: string,
    recipientName: string,
    subject: string,
    message: string,
    updates: Record<string, unknown>,
    senderId: string
  ): void => {
    const receipt: IMemberSentReceipt = {
      key: messageKey,
      toUsername: recipientName,
      sent: this.getTimestamp() as Date,
      subject: encrypt(subject),
      message: encrypt(message),
      read: false,
    };
    updates[messageKey] = receipt;
  };

  public sendMessage = async (
    message: string,
    subject: string,
    fromUserid: string,
    fromUsername: string,
    isSystemAudit: boolean,
    sentReceiptRequired: boolean,
    messageType: MessageType,
    recipients: IUserRecord[],
    ignoreSelfOwned?: boolean,
    state?: any
  ): Promise<void> => {
    const systemId = await this.lookupSystemUser();
    const sentUpdates: Record<string, unknown> = {};
    const sentRef = ref(
      this.database,
      "/users/" + fromUserid + "/sentMessages"
    );
    for (let i = 0; i < recipients.length; i += 1) {
      let { userid } = recipients[i];
      const { username } = recipients[i];
      if (userid.endsWith(memberOwnerKeySuffix))
        userid = userid.replace(memberOwnerKeySuffix, "");
      if (
        systemId &&
        systemId.userid !== fromUserid &&
        systemId.userid !== userid &&
        isSystemAudit
      ) {
        void this.pushMessageToUser(
          systemId.userid,
          fromUserid,
          fromUsername,
          message,
          subject,
          username,
          messageType,
          state
        );
      }
      if (userid === fromUserid && ignoreSelfOwned) continue; // if don't send yourself a message then return
      if (userid) {
        // eslint-disable-next-line no-await-in-loop
        const messageKey = await this.pushMessageToUser(
          userid,
          fromUserid,
          fromUsername,
          message,
          subject,
          username,
          messageType,
          state
        );
        if (userid === fromUserid || !sentReceiptRequired) continue; // no point setting a read receipt to yourself
        this.createSentReceipt(
          messageKey,
          username,
          subject,
          message,
          sentUpdates,
          fromUserid
        );
      }
    }
    void update(sentRef, sentUpdates);
  };

  private parseConfig = (snapshot: any): ISystemConfig | undefined => {
    const rootConfig = snapshot as any;
    const map = decrypt<Record<string, string>>(rootConfig, {});
    const config = decrypt<ISystemConfig | undefined>(map.chat, undefined);
    if (config)
      config.hotTopics = decrypt<Record<string, number>>(map.hotTopics, {});
    if (config && config.timeouts) {
      this.DATABASE_CONNECT_DELAY_MS =
        config.timeouts.DATABASE_CONNECT_DELAY_MS;
      this.DATABASE_RECONNECT_MS = config.timeouts.DATABASE_RECONNECT_MS;
      this.DATABASE_UPDATE_MS = config.timeouts.DATABASE_UPDATE_MS;
      this.DATABASE_TRANSACTION_TIMEOUT =
        config.timeouts.DATABASE_TRANSACTION_TIMEOUT;
    }
    return config;
  };

  public loadConfig = (
    callback: (chatConfig: ISystemConfig | undefined) => void
  ): void => {
    // The subscription's first fire delivers the initial value, so a preceding
    // bypassCache read just downloaded the whole node twice.
    const configRef = ref(this.database, "/config");
    this.on(configRef, (snapshot) => {
      if (snapshot === this.pendingResponse) return;
      callback(this.parseConfig(snapshot));
    });
  };

  public saveConfig = async (config: ISystemConfig): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates["/config/chat"] = encrypt(config);
    await this.update(updates);
  };

  private static readonly MAX_HOT_TOPICS = 50;

  public getHotTopics = async (): Promise<Record<string, number>> => {
    const data = await this.getTableSnap<string>("/hotTopics");
    if (!data) return {};
    return decrypt<Record<string, number>>(data, {}) ?? {};
  };

  public saveHotTopicsConfig = async (
    topics: Record<string, number>
  ): Promise<void> => {
    // Unbounded growth here made /config a ~600kb download for every session, to render 3 items.
    const pruned = Object.entries(topics ?? {})
      .sort(([, left], [, right]) => right - left)
      .slice(0, Database.MAX_HOT_TOPICS)
      .reduce((result, [title, count]) => {
        result[title] = count;
        return result;
      }, {} as Record<string, number>);

    const updates: Record<string, unknown> = {};
    updates["/hotTopics"] = encrypt(pruned);
    updates["/config/hotTopics"] = null; // retire the oversized legacy node
    await this.update(updates);
  };

  public clearLogs = async (userid: string): Promise<void> => {
    await this.remove(ref(this.database, `/audit/logs/${userid}`));
  };

  public payOwner = async (userid: string, amount: number): Promise<void> => {
    const control = await this.getTableSnap<IControl>(
      "/control/" + userid,
      true
    );
    if (!control || !amount) return;
    const dbUpdates: Record<string, unknown> = {};
    dbUpdates["/control/" + userid + "/bank"] = control.bank + amount;
    dbUpdates["/users/" + userid + "/sessionProgressState/bank"] =
      (control.bank ?? 0) + amount;
    await this.update(dbUpdates);
  };

  // updates the owned items for a user, saving off the items in the credit item
  // collection for the items which are set to be owned. this allows the same options
  // to be applied to the items in another session
  // when determining options for an item the options on the actual item are overriden
  // by the options the user has set for the item
  public updateOwnedItem = async (
    userid: string,
    item: ITaskItem,
    owned: boolean
  ): Promise<void> => {
    if (owned) {
      const updates: Record<string, unknown> = {};
      updates["/users/" + userid + "/ownedItems/" + item.key] = encrypt(
        item.options ?? []
      );
      await this.update(updates);
    } else {
      void this.remove(
        ref(this.database, "/users/" + userid + "/ownedItems/" + item.key)
      );
    }
  };

  /* called from the copy template, copy profile */
  public addOwnerProfile = async (
    profile: IOwnerProfileSession,
    settings: ISessionSettings
  ): Promise<void> => {
    const updates: Record<string, unknown> = {};
    const json = encrypt(profile);
    const strippedSettings = stripSettings(
      settings,
      undefined,
      this.systemSettings,
      false,
      profile.userid,
      this
    );
    updates["/ownerProfiles/" + profile.userid] = json;
    Object.assign(
      updates,
      buildStructuredSettingsUpdates(
        profile.userid,
        Database.migrateSettings(
          profile.userid,
          cloneObject(strippedSettings) as ISessionSettings,
          this
        )
      )
    );
    // tidy up any user ids for the old style user profiles
    void this.remove(
      ref(
        this.database,
        "/ownerProfiles/" + profile.userid.replace("-ownerProfile", "")
      )
    );
    await this.update(updates);
  };

  /* called from setting screens when the profile is first created by owners and system admin from edit settings tab */
  public saveOwnerProfile = async (
    profile: IOwnerProfileSession
  ): Promise<void> => {
    const updates: Record<string, unknown> = {};
    const json = encrypt(profile);
    updates["/ownerProfiles/" + profile.userid] = json;
    await this.update(updates);
  };

  // removes the key from the stored objects
  public removeOwnerProfile = async (
    userid: string,
    removeSettings: boolean
  ): Promise<void> => {
    await this.remove(ref(this.database, "/ownerProfiles/" + userid));
    if (removeSettings) {
      await this.remove(ref(this.database, "/users/" + userid));
    }
  };

  public snapshotGame = (): Promise<Record<string, IGame>> => {
    const record = new CacheRecord<IGame>(GamePath, this);
    return record.snapshot(undefined);
  };

  /** Real-time subscription to lightweight game summaries (no subtype-specific data). */
  public snapAndSubGameSummaries = (userid: string, summariesUpdated: (snapshot: Record<string, IGame>) => void): Unsubscribe => {
    const summaryRef = ref(this.database, `/${GameSummaryPath}/`);
    return this.on(summaryRef, summariesUpdated);
  };

  /** Kept for compatibility — subscribes to full game records. */
  public snapAndSubGames = (userid: string, gamesUpdated: (snapshot: Record<string, IGame>) => void
  ): Unsubscribe => {
    const record = new CacheRecord<IGame>(GamePath, this);
    return record.snapAndSub(undefined, gamesUpdated);
  }

  public updateGame = async (userid: string, game: IGame): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates[`/${GamePath}/${game.key}`] = encrypt(game);
    updates[`/${GameSummaryPath}/${game.key}`] = encrypt(toGameSummary(game));
    await this.update(updates);
  };

  public removeGame = async (userid: string, gameKey: string): Promise<void> => {
    const deletions: Record<string, null> = {
      [`/${GamePath}/${gameKey}`]: null,
      [`/${GameSummaryPath}/${gameKey}`]: null,
    };
    await update(ref(this.database), deletions);
  };

  /** Fetch the full game detail (with subtype-specific data) for a single game key. */
  public fetchGameDetail = async (key: string): Promise<IGame | undefined> => {
    const data = await this.getTableSnap<string>(`/${GamePath}/${key}`);
    if (!data) return undefined;
    return decrypt(data, {} as IGame) as IGame;
  };

  /**
   * Migration: if there are no game summaries yet but full records exist,
   * generate summaries from them. Subsequent calls are no-ops.
   */
  public migrateGameSummaries = async (): Promise<void> => {
    const existing = await this.getTableSnap<Record<string, unknown>>(`/${GameSummaryPath}/`);
    if (existing && Object.keys(existing).length > 0) return; // already migrated
    const detailData = await this.getTableSnap<Record<string, string>>(`/${GamePath}/`);
    if (!detailData || Object.keys(detailData).length === 0) return; // nothing to migrate
    const updates: Record<string, unknown> = {};
    Object.keys(detailData).forEach((key) => {
      const game = decrypt(detailData[key], {} as IGame) as IGame;
      updates[`/${GameSummaryPath}/${key}`] = encrypt(toGameSummary(game));
    });
    await this.update(updates);
  };

  public snapAndSubOwnerProfiles = (
    userid: string | undefined,
    profilesUpdated: (snapshot: any) => void
  ): Unsubscribe => {
    loadPerf(`Database.snapAndSubOwnerProfiles subscribed`);
    let _profilesFirstFire = true;
    const wrappedProfilesUpdated = (snapshot: any) => {
      if (_profilesFirstFire) {
        _profilesFirstFire = false;
        loadPerf(`Database.snapAndSubOwnerProfiles first callback`, { keys: Object.keys(snapshot ?? {}).length });
      }
      profilesUpdated(snapshot);
    };
    if (!userid) {
      const stateRef = ref(this.database, "/ownerProfiles/");
      return this.on(stateRef, wrappedProfilesUpdated);
    }
    const ownerKey = getOwnerUserId(userid);
    const stateRef = ref(this.database, `/ownerProfiles/${ownerKey}`);
    return this.on(stateRef, (encryptedValue: any) => {
      wrappedProfilesUpdated(encryptedValue ? { [ownerKey]: encryptedValue } : {});
    });
  };

  public fetchAllOwnerProfiles = async (): Promise<IOwnerProfileSession[]> => {
    // snapAndSubOwnerProfiles keeps this node live, so read through the cache.
    const data = await this.getTableSnap<Record<string, string>>("/ownerProfiles/");
    if (!data) return [];
    return Object.values(data)
      .map(str => decrypt(str, {} as IOwnerProfileSession) as IOwnerProfileSession)
      .filter(p => p && p.userid);
  };

  public snapOwnerProfile = async (userid: string): Promise<string> => {
    return await this.getTableSnap<string>("ownerProfiles/" + userid);
  };

  private static readonly IMAGE_CACHE_PREFIX = "__tarlings_img_";

  public getImage = async (imgId: string): Promise<string> => {
    if (!imgId) return "";
    // imageDb entries are immutable uuid -> url pairs, so they never need refetching.
    const cacheKey = Database.IMAGE_CACHE_PREFIX + imgId;
    const cached =
      typeof localStorage !== "undefined" ? localStorage.getItem(cacheKey) : null;
    if (cached !== null) return cached;

    const url = await this.getTableSnap<string>("imageDb/" + imgId);
    if (url && typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(cacheKey, url);
      } catch {
        // storage full - fall back to fetching each session
      }
    }
    return url;
  };

  public snapAndSubSessionProgressState = (
    userid: string,
    callback: (update: ISessionProgressState) => void
  ): Unsubscribe => {
    const stateRef = ref(
      this.database,
      `/users/${userid}/sessionProgressState`
    );
    return this.on(stateRef, callback);
  };

  public updateSessionProgressState = async (control: IControl) => {
    const updates = this.buildChangedNodeUpdates(
      `/users/${control.userid}/sessionProgressState`,
      {
        bank: control.bank ?? 0,
        penaltyPoints: control.penaltyPoints ?? 0,
        lastPenalties: control.lastPenalties ?? 0,
        lastScore: control.lastScore ?? 0,
        totalPenalties: control.totalPenalties ?? 0,
        totalScore: control.totalScore ?? 0,
      } as unknown as Record<string, unknown>
    );
    if (Object.keys(updates).length === 0) return;
    await this.update(updates);
  };

  public snapAndSubOwnershipControl = (
    userid: string,
    callback: (update: IOwnershipControl) => void
  ): Unsubscribe => {
    const stateRef = ref(this.database, `/ownershipControl/${userid}`);
    return this.on(stateRef, callback);
  };

  public updateOwnershipControl = async (control: IControl) => {
    const updates = this.buildChangedNodeUpdates(
      `/ownershipControl/${control.userid}`,
      {
        userid: control.userid,
        username: control.username,
        loanedOwner: control.loanedOwner ?? null,
        loanedOwnerid: control.loanedOwnerid ?? null,
        otherOwnerIds: control.otherOwnerIds ?? null,
        owner: control.owner ?? null,
        ownerid: control.ownerid ?? null,
        virtualProfileId: control.virtualProfileId ?? null,
        virtualProfileImage: control.virtualProfileImage ?? null,
        virtualProfileName: control.virtualProfileName ?? null,
      } as unknown as Record<string, unknown>
    );
    if (Object.keys(updates).length === 0) return;
    await this.update(updates);
  };

  public heartbeatControl = async (
    userid: string,
    heartbeat: string
  ): Promise<void> => {
    if (!userid) return;
    const updates: Record<string, unknown> = {};
    const lastUpdateTime = this.getTimestamp() as Date;
    updates[`/control/${userid}/lastUpdateTime`] = lastUpdateTime;
    updates[`/control/${userid}/heartbeat`] = heartbeat;
    await this.update(updates);
  };

  // Tracks the payload this client last wrote to a node so repeat writes only send
  // changed leaves. Rewriting whole records broadcast fat fields (profile images,
  // categories) to every subscriber on every bank/score tick.
  private lastNodeWrites: Record<string, Record<string, unknown>> = {};

  private buildChangedNodeUpdates = (
    nodePath: string,
    next: Record<string, unknown>,
    alwaysWriteKeys: string[] = []
  ): Record<string, unknown> => {
    const previous = this.lastNodeWrites[nodePath];
    this.lastNodeWrites[nodePath] = { ...next };
    if (!previous) return { [nodePath]: next };

    const updates: Record<string, unknown> = {};
    Object.keys(next).forEach((key) => {
      if (
        alwaysWriteKeys.includes(key) ||
        JSON.stringify(previous[key]) !== JSON.stringify(next[key])
      )
        updates[`${nodePath}/${key}`] = next[key];
    });
    Object.keys(previous).forEach((key) => {
      if (!(key in next)) updates[`${nodePath}/${key}`] = null;
    });
    return updates;
  };

  public updateControl = async (
    userid: string,
    control: ControlRecord,
    isSystem: boolean
  ): Promise<void> => {
    const updateSensitiveFields =
      !isSystem || (this.systemUser && userid === this.systemUser.userid);
    const newLocation = await getLocation();
    const controlRecord = {
      bank: control.bank,
      penaltyPoints: control.penaltyPoints,
      heartbeat: control.heartbeat,
      loanedOwnerid: control.loanedOwnerid,
      otherOwnerIds: control.otherOwnerIds,
      userid: control.userid,
      loanedOwner: control.loanedOwner,
      ownerid: control.ownerid,
      isAdmin: control.isAdmin,
      categories: control.categories,
      owner: control.owner,
      lastUpdateTime: control.lastUpdateTime,
      created: control.created,
      username: control.username,
      alias: control.alias,
      isActive: control.isActive,
      lastPenalties: control.lastPenalties,
      totalPenalties: control.totalPenalties,
      lastScore: control.lastScore,
      totalScore: control.totalScore,
      missingCategories: control.missingCategories,
      mutedMessageTypes: control.mutedMessageTypes,
      mutedNotificationTypes: control.mutedNotificationTypes,
      profileImage: control.profileImage,
      role: control.role,
      summary: control.summary,
      virtualProfileImage: control.virtualProfileImage,
      virtualProfileName: control.virtualProfileName,
      virtualProfileId: control.virtualProfileId,
      lovenseSync: control.lovenseSync,
      displayMode: control.displayMode,
      disablePublicProofs: control.disablePublicProofs ? true : false,
      location: control.location ?? {},
      country: control.country,
      discordUserId: control.discordUserId,
      language: control.language,
    } as IControl;

    if (updateSensitiveFields) {
      if (!controlRecord.location) controlRecord.location = {};
      if (Array.isArray(controlRecord.location))
        controlRecord.location = {
          city: (controlRecord.location as string[])[0] ?? "",
          region: (controlRecord.location as string[])[1] ?? "",
          country_name: (controlRecord.location as string[])[2] ?? "",
          longitude: (controlRecord.location as string[])[3] ?? "",
          latitude: (controlRecord.location as string[])[4] ?? "",
        };
      Object.keys(newLocation)
        .filter((k) => newLocation[k] !== undefined)
        .forEach((k) => {
          if (!controlRecord.location) controlRecord.location = {};
          controlRecord.location[k] = newLocation[k];
        });
    }

    if (!userid || !controlRecord.username) return;
    if (updateSensitiveFields)
      controlRecord.lastUpdateTime = this.getTimestamp() as Date;
    if (controlRecord.isAdmin === undefined) controlRecord.isAdmin = false;
    if (controlRecord.bank === undefined) controlRecord.bank = 0;
    if (!controlRecord.owner) delete controlRecord.owner;
    if (controlRecord.penaltyPoints === undefined)
      controlRecord.penaltyPoints = 0;
    if (!controlRecord.ownerid) delete controlRecord.ownerid;
    if (!controlRecord.loanedOwner) delete controlRecord.loanedOwner;
    if (!controlRecord.loanedOwnerid) delete controlRecord.loanedOwnerid;
    if (!controlRecord.otherOwnerIds) delete controlRecord.otherOwnerIds;
    if (!controlRecord.alias) delete controlRecord.alias;
    if (!controlRecord.virtualProfileId) delete controlRecord.virtualProfileId;
    if (!controlRecord.virtualProfileName)
      delete controlRecord.virtualProfileName;
    if (!controlRecord.virtualProfileImage)
      delete controlRecord.virtualProfileImage;
    if (!controlRecord.summary) delete controlRecord.summary;
    else controlRecord.summary = encrypt(controlRecord.summary);
    if (!controlRecord.categories) delete controlRecord.categories;
    else if (typeof controlRecord.categories === "object")
      controlRecord.categories = encrypt(controlRecord.categories) as any;
    if (!controlRecord.isActive) delete controlRecord.isActive;
    if (!controlRecord.lastPenalties) delete controlRecord.lastPenalties;
    if (!controlRecord.totalPenalties) delete controlRecord.totalPenalties;
    if (!controlRecord.lastScore) delete controlRecord.lastScore;
    if (!controlRecord.totalScore) delete controlRecord.totalScore;
    if (!controlRecord.profileImage) delete controlRecord.profileImage;
    if (!controlRecord.missingCategories)
      delete controlRecord.missingCategories;
    else if (typeof controlRecord.missingCategories === "object")
      controlRecord.missingCategories = encrypt(
        controlRecord.missingCategories
      ) as any;
    if (!controlRecord.mutedMessageTypes)
      delete controlRecord.mutedMessageTypes;
    if (!controlRecord.mutedNotificationTypes)
      delete controlRecord.mutedNotificationTypes;
    if (!controlRecord.role) delete controlRecord.role;
    if (
      !controlRecord.location ||
      Object.keys(controlRecord.location).length === 0
    )
      delete controlRecord.location;
    if (!controlRecord.created) delete controlRecord.created;
    if (!controlRecord.lovenseSync) delete controlRecord.lovenseSync;
    if (!controlRecord.displayMode) delete controlRecord.displayMode;
    if (!controlRecord.discordUserId) delete controlRecord.discordUserId;
    if (!controlRecord.language) delete controlRecord.language;
    if (!controlRecord.country) delete controlRecord.country;
    if (!controlRecord.heartbeat)
      controlRecord.heartbeat = import.meta.env.VITE_VERSION ?? "0.0.0.0";

    const updates = this.buildChangedNodeUpdates(
      "/control/" + userid,
      controlRecord as unknown as Record<string, unknown>,
      // serverTimestamp is an opaque sentinel, so it never diffs as changed
      updateSensitiveFields ? ["lastUpdateTime"] : []
    );

    if (Object.keys(updates).length > 0) await this.update(updates);
    await this.updateSessionProgressState(controlRecord); // eventually we will replace the calls in control record with just this
    await this.updateOwnershipControl(controlRecord); // eventually we will replace the calls in control record with just this

    if (import.meta.env.PROD && (await isSupported())) {
      logEvent(getAnalytics(), "screen_view");
    }
  };

  public updateUserName = async (
    userid: string,
    username: string
  ): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates["/control/" + userid + "/username"] = username;
    await this.update(updates);
  };

  public updatePilloryTopic = async (topic: PilloryTopic): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates["/pillory/" + topic.id] = encrypt(topic);
    await this.update(updates);
  };

  public snapAndSubPillory = (
    pilloryUpdated: (pillories: any) => void
  ): Unsubscribe => {
    const pilloryRef = ref(this.database, "pillory");
    return this.on(pilloryRef, pilloryUpdated);
  };

  public clearCompletePillories = (pillories: PilloryTopic[]) => {
    pillories.forEach(
      (p) => void this.remove(ref(this.database, `/pillory/${p.id}`))
    );
  };

  /*
  the idea is that when we start a task which is votable we can store the expected end time
  with the task id for the running task for the user, this then is open to voting like anything else
  when a vote task has been voted on, it increments the end time for that task and then 
  when the user next logs on their task running state is updated with any task vote info which is available
  to ensure it picks up any increases since they were last logged on. It should be simple to pull the
  vote sessions from the state for the user and find the corresponding task runnning for the task key
  and then apply the new end date and time and work out the current ticks again based on the new values
  */
  public updateVoteTask = async (voteTask: VoteTask): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates[`/voteTasks/${voteTask.userid}/${voteTask.id}`] = encrypt(voteTask);
    updates[`/voteTaskKeys/${voteTask.id}`] = {
      lastUpdateDate: new Date(),
      userid: voteTask.userid,
    };
    await this.update(updates);
  };

  voteSubscriptions: Record<string, Record<string, Unsubscribe>> = {};
  /*
  the uuid here is so that we can listen to new votes coming in which are expensive and query them alone
  and listen to updates, so we don't get the full set of updates every time there is a vote
  */
  public snapAndSubVoteTasks = (
    voteTasksUpdated: (voteTask: string) => void,
    uuid: string
  ): Unsubscribe => {
    const voteTaskKeysRef = ref(this.database, "voteTaskKeys");
    if (!this.voteSubscriptions[uuid]) this.voteSubscriptions[uuid] = {};
    this.on(
      voteTaskKeysRef,
      (newKeys: Record<string, { lastUpdateDate: Date; userid: string }>) => {
        Object.keys(newKeys).forEach((key) => {
          if (this.voteSubscriptions[uuid][key]) return; // we have this already, don't resubscribe.
          const voteTasksRef = ref(
            this.database,
            "voteTasks/" + newKeys[key].userid + "/" + key
          );
          this.voteSubscriptions[uuid][key] = this.on(
            voteTasksRef,
            voteTasksUpdated
          );
        });
      }
    );
    return () => {
      if (!this.voteSubscriptions[uuid]) return;
      Object.keys(this.voteSubscriptions[uuid]).forEach((key) =>
        this.voteSubscriptions[uuid][key]()
      );
      this.voteSubscriptions = {};
    };
  };

  public clearCompleteVoteTasks = (voteTasks: VoteTask[]) => {
    voteTasks.forEach((p) => {
      void this.remove(ref(this.database, `voteTasks/${p.userid}/${p.id}`));
      void this.remove(ref(this.database, `voteTaskKeys/${p.id}`));
    });
  };

  public clearPausedTaskLocks = (taskLocks: ILockReceipt[]) => {
    taskLocks.forEach(
      (l) =>
        void this.remove(ref(this.database, `locks/${l.userid}/${l.imageKey}`))
    );
  };

  public getProfileSettings = async (
    userid: string
  ): Promise<ISessionSettings> => {
    const profileSettings = await this.getUserSettings(userid);
    return mergeSettings(
      profileSettings,
      undefined,
      this.systemSettings,
      false
    );
  };

  public snapAndSubAttendanceRequests = (
    userid: string,
    cb: (attendanceRequest: IAttendanceRequest) => void
  ): Unsubscribe => {
    const requestsRef = ref(this.database, "/attendanceRequests/" + userid);
    return this.on(requestsRef, (attendanceRequestString) => {
      if (attendanceRequestString === this.pendingResponse) return; // not yet ready but call is in flight, return response when it is ready
      const attendanceRequest = decrypt(
        attendanceRequestString,
        undefined as any as IAttendanceRequest
      ) as IAttendanceRequest;
      cb(attendanceRequest);
    });
  };

  public snapAndSubAttendanceRequestImages = (
    userid: string,
    cb: (imageSrc: string) => void
  ): Unsubscribe => {
    const requestsRef = ref(
      this.database,
      "/attendanceRequests/images/" + userid
    );
    return this.on(requestsRef, (attendanceRequestString) => {
      if (attendanceRequestString === this.pendingResponse) return; // not yet ready but call is in flight, return response when it is ready
      const attendanceRequest = attendanceRequestString?.toString();
      cb(attendanceRequest);
    });
  };

  public snapAndSubAllAttendanceRequests = (
    cb: (attendanceRequest: Record<string, IAttendanceRequest>) => void
  ): Unsubscribe => {
    const requestsRef = ref(this.database, "/attendanceRequests/");
    return this.on(requestsRef, (attendanceRequestsPayload) => {
      const returnData: Record<string, IAttendanceRequest> = {};
      if (attendanceRequestsPayload) {
        Object.keys(attendanceRequestsPayload).forEach((p) => {
          returnData[p] = decrypt(
            attendanceRequestsPayload[p] as string,
            undefined as any as IAttendanceRequest
          ) as IAttendanceRequest;
        });
      }
      cb(returnData);
    });
  };

  public saveAttendanceRequest = async (
    profile: ControlRecord,
    positionKey: string,
    penaltyKey: string,
    endTime: Date,
    handled?: boolean,
    holdDurationSec?: number,
    releaseTimeUtc?: string
  ): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates[`/attendanceRequests/${profile.userid}`] = encrypt({
      positionKey,
      penaltyKey,
      endTimeUtc: endTime.toUTCString(),
      handled: handled ?? false,
      holdDurationSec,
      releaseTimeUtc,
    } as IAttendanceRequest);
    return await this.update(updates);
  };

  public updateAttendanceRequestImage = async (
    imageSrc: string,
    profile: ControlRecord
  ) => {
    const updates: Record<string, unknown> = {};
    updates[`/attendanceRequests/images/${profile.userid}/`] = imageSrc;
    return await this.update(updates);
  };

  public updateAttendanceRequest = async (
    profile: ControlRecord,
    positionKey: string,
    penaltyKey: string,
    endTime: string,
    handled?: boolean,
    fullScreen?: boolean,
    lastMessage?: string,
    holdDurationSec?: number,
    releaseTimeUtc?: string
  ): Promise<void> => {
    const updates: Record<string, unknown> = {};
    updates[`/attendanceRequests/${profile.userid}`] = encrypt({
      positionKey,
      penaltyKey,
      endTimeUtc: endTime,
      handled: handled ?? false,
      fullScreen: fullScreen ?? false,
      lastMessage: lastMessage ?? "",
      holdDurationSec,
      releaseTimeUtc,
    } as IAttendanceRequest);
    return await this.update(updates);
  };

  public cancelAttendanceRequest = (userid: string): void => {
    void this.remove(ref(this.database, `attendanceRequests/${userid}`));
    void this.remove(ref(this.database, `attendanceRequests/images/${userid}`));
  };

  public reallocateUserId = async (
    currentUser: IUserRecord,
    newUserCreds: UserCredential
  ): Promise<void> => {
    const newUser = { ...currentUser, userid: newUserCreds.user.uid };
    // delete the current auth record for the userid
    const updates: Record<string, unknown> = {};

    // we take all the settings from current and we set them for the new user id
    // this handles state, messages, settings
    const currentSettings = await this.getTableSnap<any>(
      "users/" + currentUser.userid
    );
    updates["users/" + newUser.userid] = currentSettings;

    const control = await this.snapControlRecord(currentUser.userid);
    const controlRecord = new ControlRecord(control);
    controlRecord.userid = newUser.userid;
    controlRecord.username = newUser.username; // this will update the user so their user name in the system matches the username of the log on
    void this.updateControl(newUser.userid, controlRecord, true);

    // owner profiles
    const profileStr = await this.getTableSnap<string>(
      "ownerProfiles/" + getOwnerUserId(currentUser.userid)
    );
    if (profileStr) {
      const profile = decrypt(profileStr, {}) as IOwnerProfileSession;
      if (profile) {
        const profileSettings = await this.getUserSettings(
          getOwnerUserId(currentUser.userid)
        );
        if (profileSettings) {
          profile.userid = newUser.userid;
          void this.addOwnerProfile(profile, profileSettings);
        }
      }
    }

    const users = await this.getUsers();


    // where we are an owner of...
    // where we are a sub of...
    // remove any references to this user from other sessions
    for (let i = 0; i < Object.keys(users).length; i += 1) {
      const userKey = Object.keys(users)[i];
      if (userKey === currentUser.userid) continue;
      const profile = users[userKey];
      if (!profile) continue;
      if (profile.ownerid === currentUser.userid) {
        updates["/control/" + userKey + "/owner"] = newUser.username;
        updates["/control/" + userKey + "/ownerid"] = newUser.userid;
        updates["/ownershipControl/" + userKey + "/owner"] = newUser.username;
        updates["/ownershipControl/" + userKey + "/ownerid"] = newUser.userid;
      }

      if (profile.virtualProfileId === getOwnerUserId(currentUser.userid)) {
        updates["/control/" + userKey + "/virtualProfileName"] =
          newUser.username;
        updates["/control/" + userKey + "/virtualProfileId"] = getOwnerUserId(
          newUser.userid
        );
        updates["/ownershipControl/" + userKey + "/virtualProfileName"] =
          newUser.username;
        updates["/ownershipControl/" + userKey + "/virtualProfileId"] =
          getOwnerUserId(newUser.userid);
      }

      if (profile.loanedOwnerid === currentUser.userid) {
        updates["/control/" + userKey + "/loanedOwner"] = newUser.username;
        updates["/control/" + userKey + "/loanedOwnerid"] = newUser.userid;
        updates["/ownershipControl/" + userKey + "/loanedOwner"] =
          newUser.username;
        updates["/ownershipControl/" + userKey + "/loanedOwnerid"] =
          newUser.userid;
      }
      if (
        profile.otherOwnerIds &&
        profile.otherOwnerIds.includes(currentUser.userid)
      ) {
        profile.otherOwnerIds.splice(
          profile.otherOwnerIds.indexOf(currentUser.userid),
          1
        );
        profile.otherOwnerIds.push(newUser.userid);
        updates["/control/" + userKey + "/otherOwnerIds"] =
          profile.otherOwnerIds;
        updates["/ownershipControl/" + userKey + "/otherOwnerIds"] =
          profile.otherOwnerIds;
      }
    }

    // audits
    const auditLogs = await this.getTableSnap<any>(
      `/audit/logs/${currentUser.userid}`
    );
    const auditScores = await this.getTableSnap<any>(
      `/audit/scores/${currentUser.userid}`
    );
    const auditOwners = await this.getTableSnap<any>(
      `/audit/owners/${currentUser.userid}`
    );
    const auditBank = await this.getTableSnap<any>(
      `/audit/bank/${currentUser.userid}`
    );
    updates[`/audit/logs/${newUser.userid}`] = auditLogs;
    updates[`/audit/scores/${newUser.userid}`] = auditScores;
    updates[`/audit/owners/${newUser.userid}`] = auditOwners;
    updates[`/audit/bank/${newUser.userid}`] = auditBank;

    // pillories, votes and locks
    const locks = await this.getTableSnap<
      Record<string, Record<string, ILockReceipt>>
    >("/locks/");
    if (locks) {
      for (let i = 0; i < Object.keys(locks).length; i += 1) {
        const lockKey = Object.keys(locks)[i];
        const lockTaskCollection = locks[lockKey];
        for (let i = 0; i < Object.keys(lockTaskCollection).length; i += 1) {
          const lockTaskKey = Object.keys(lockTaskCollection)[i];
          const lockTask = lockTaskCollection[lockTaskKey];
          if (lockTask.userid === currentUser.userid) {
            lockTask.userid = newUser.userid;
            updates[`/locks/${newUser.userid}/${lockTaskKey}`] = lockTask;
          } else if (lockTask.ownerid === currentUser.userid) {
            lockTask.ownerid = newUser.userid;
            updates[`/locks/${lockKey}/${lockTaskKey}`] = lockTask;
          }
        }
        if (lockKey === currentUser.userid) {
          void this.remove(ref(this.database, `/locks/${currentUser.userid}`));
        }
      }
    }

    // proofRequests are transient signals only — no fields to migrate, just clean up the old user path
    void this.remove(ref(this.database, `/proofRequests/${currentUser.userid}`));

    const pillories = await this.getTableSnap<Record<string, string>>(
      "/pillory/"
    );
    if (pillories) {
      for (let i = 0; i < Object.keys(pillories).length; i += 1) {
        const pilloryKey = Object.keys(pillories)[i];
        const pillory = decrypt(pillories[pilloryKey], {}) as IPilloryTopic;
        if (pillory.userid === currentUser.userid) {
          pillory.userid = newUser.userid;
          updates[`/pillory/${pilloryKey}`] = encrypt(pillory);
        }
      }
    }

    const votes = await this.getTableSnap<
      Record<string, Record<string, string>>
    >("/voteTasks/");
    if (votes) {
      for (let i = 0; i < Object.keys(votes).length; i += 1) {
        const userId = Object.keys(votes)[i];
        if (userId === currentUser.userid) {
          const userVotes = votes[userId];
          Object.keys(userVotes).forEach((userVoteKey) => {
            const userVoteStr = userVotes[userVoteKey];
            const vote = decrypt<IVoteTask>(userVoteStr, {} as IVoteTask);
            vote.userid = newUser.userid;
            userVotes[userVoteKey] = encrypt(vote);
          });
          updates[`/voteTasks/${newUser.userid}`] = userVotes;
          void this.remove(
            ref(this.database, `/voteTasks/${currentUser.userid}`)
          );
        }
      }
    }
    await this.update(updates);

    // finally we can delete the currentuser
    await this.deleteUser(currentUser.userid);
  };

  public removeTaskLock = (lock: IRequestLockState, userid: string) => {
    void this.remove(
      ref(this.database, `/locks/${userid}/${lock.result?.imageKey}`)
    );
  };

  public static migrateSettings = (
    userid: string,
    settings: ISessionSettings,
    database?: Database,
    ownerSettings?: ISessionSettings
  ): ISessionSettings => {
    if (!settings) return settings;

    const systemUserId = database?.systemUser?.userid;
    const isSystemRecord = !!systemUserId && userid.startsWith(systemUserId);
    const parentSettings = ownerSettings ?? database?.systemSettings;
    const normalizeItems = (
      items?: ISettingsItem[],
      parentItems?: ISettingsItem[]
    ) => {
      if (!Array.isArray(items)) return;
      for (let idx = items.length - 1; idx >= 0; idx -= 1) {
        const item = items[idx] as any;
        if (!item || typeof item !== "object") {
          items.splice(idx, 1);
          continue;
        }

        delete item.isOwner;
        delete item.isOverride;

        if (Array.isArray(item.categories)) {
          item.categories = item.categories.filter(
            (value: unknown) => typeof value === "string" && value.length > 0
          );
        }
        if (Array.isArray(item.optionImages)) {
          item.optionImages = item.optionImages.filter(Boolean);
        }
        if (Array.isArray(item.itemsUsed)) {
          item.itemsUsed = item.itemsUsed
            .filter((set: unknown) => Array.isArray(set))
            .map((set: unknown[]) =>
              set.filter(
                (value: unknown) => typeof value === "string" && value.length > 0
              )
            )
            .filter((set: string[]) => set.length > 0);
        }

        const parentMatch = parentItems?.find((parent) => parent?.key === item.key);
        const shouldClearApprovedParentFlags =
          !!parentMatch && !parentMatch.isDeleted && parentMatch.requiresValidation !== true;

        const shouldClearSystemApprovalFlags =
          (isSystemRecord || item.isSystem === true) &&
          (!item.createdBy || item.createdBy === userid || item.createdBy === systemUserId);

        if (shouldClearApprovedParentFlags || shouldClearSystemApprovalFlags) {
          item.requiresValidation = false;
          item.isNew = false;
        } else if ((isSystemRecord || item.isSystem === true) && item.requiresValidation !== true) {
          item.isNew = false;
        }
      }
    };

    normalizeItems(
      settings.creditSettings?.credits as ISettingsItem[],
      parentSettings?.creditSettings?.credits as ISettingsItem[]
    );
    normalizeItems(
      settings.penaltySettings?.penalties as ISettingsItem[],
      parentSettings?.penaltySettings?.penalties as ISettingsItem[]
    );
    normalizeItems(
      settings.positionSettings?.positions as ISettingsItem[],
      parentSettings?.positionSettings?.positions as ISettingsItem[]
    );
    normalizeItems(
      settings.permissionSettings?.permissions as ISettingsItem[],
      parentSettings?.permissionSettings?.permissions as ISettingsItem[]
    );
    normalizeItems(
      settings.eventsSettings?.events as ISettingsItem[],
      parentSettings?.eventsSettings?.events as ISettingsItem[]
    );

    return settings;
  };

  public static copyCustomSettingsToSystem = (
    userid: string,
    settings: ISessionSettings,
    system: ISessionSettings,
    database?: Database
  ): boolean => {
    // handle updates to system settings when member settings available
    if (!settings || !database || !system || !database.systemUser) return false;
    let updated = false;
    const copySettings = (
      user: (ISettingsItem & ICategoryItem)[],
      sys: (ISettingsItem & ICategoryItem)[],
      collectionType: ItemTypes
    ) => {
      if (user && sys) {
        user.forEach((i) => {
          const existing = sys.find((ii) => ii.key === i.key);
          if (!existing) return;

          const hydratedItem = hydrateItemFromCreator(existing, i) as ISettingsItem & ICategoryItem;
          let hydrated = JSON.stringify(hydratedItem) !== JSON.stringify(existing);
          Object.assign(existing, hydratedItem);

          const creatorItemHandled = i.createdBy === userid && isCreatorItemHandled(i);
          const systemItemIsStalePending = existing.createdBy === userid
            && existing.requiresValidation === true
            && creatorItemHandled;

          if (systemItemIsStalePending) {
            existing.requiresValidation = false;
            existing.isNew = false;
            hydrated = true;
          } else if (existing.isNew === true && existing.requiresValidation !== true && creatorItemHandled) {
            existing.isNew = false;
            hydrated = true;
          }

          if (hydrated) updated = true;
        });

        const missingCredits = user.filter(
          (i) => !sys.find((ii) => ii.key === i.key) && !i.isDeleted
        );
        missingCredits.forEach((i) => {
          if (i.requiresValidation) return; // it is already pending with the system so ignore for now - we might want to delete these ones eventually??
          if (
            (i as any).eventType &&
            (i as any).eventType !== EventType.standard
          )
            return;
          const copyItem = { ...i };
          copyItem.isNew = true; // user level flag which will be overriden when they make their choice
          copyItem.requiresValidation = true;
          copyItem.createdDate = new Date();
          copyItem.createdBy = userid;
          copyItem.isCustom = false;
          copyItem.isSystem = true;
          copyItem.isOwner = false;
          copyItem.isOverride = false;
          copyItem.itemType = collectionType;
          sys.push(copyItem);
          if (!i.hidden) i.hidden = false; /// means we override the hidden main one with enabled for the member who had it
          updated = true;
        });
      }
    };

    if (settings.creditSettings && system.creditSettings)
      copySettings(
        settings.creditSettings.credits,
        system.creditSettings.credits,
        ItemTypes.task
      );
    if (settings.penaltySettings && system.penaltySettings)
      copySettings(
        settings.penaltySettings.penalties,
        system.penaltySettings.penalties,
        ItemTypes.penalty
      );
    if (settings.permissionSettings && system.permissionSettings)
      copySettings(
        settings.permissionSettings.permissions,
        system.permissionSettings.permissions,
        ItemTypes.permission
      );
    if (settings.eventsSettings && system.eventsSettings)
      copySettings(
        settings.eventsSettings.events,
        system.eventsSettings.events,
        ItemTypes.event
      );
    if (settings.positionSettings && system.positionSettings)
      copySettings(
        settings.positionSettings.positions,
        system.positionSettings.positions,
        ItemTypes.position
      );
    if (updated) {
      if (
        system.creditSettings.credits.length === 0 ||
        system.positionSettings.positions.length === 0 ||
        system.permissionSettings.permissions.length === 0 ||
        system.penaltySettings.penalties.length === 0 ||
        system.eventsSettings.events.length === 0
      ) {
        Emitter.emitNotification({
          message: "System defaults are empty! Contact Admin",
          fatal: true,
          persist: true,
          notificationType: NotificationType.ErrorInfo,
        });
        return false;
      }
      void database.updateSettings(database.systemUser.userid, system, true);
      void database.updateSettings(userid, settings, true);
    }
    return updated;
  };

  public static migrateControl = (control: IControl): void => {
    if (!control) return;
    if ((control as any).memberImage) {
      if (!control.profileImage)
        control.profileImage = (control as any).memberImage;
      delete (control as any).memberImage;
    }
    if (control.penaltyPoints === undefined) control.penaltyPoints = 0;
    Database.decryptControlRecord(control);
  };

  public static decryptControlRecord = (record: IControl) => {
    if (record.summary)
      record.summary = decrypt(record.summary, record.summary);
    if (record.categories)
      record.categories = decrypt<string[]>(
        record.categories as unknown as string,
        record.categories
      );
    if (record.missingCategories)
      record.missingCategories = decrypt<string[]>(
        record.missingCategories as unknown as string,
        record.missingCategories
      );
  };

  public addCustomSchedule = async (
    userid: string,
    schedule: ICustomSchedule
  ): Promise<void> => {
    const updates: Record<string, unknown> = {};
    const meaningfulEvents = (schedule.events ?? []).filter(
      (e) => Boolean(e.name?.trim() || e.minTime || e.maxTime)
    );
    const summary: ICustomSchedule = {
      ...schedule,
      eventCount: meaningfulEvents.length,
      summaryMinTime: meaningfulEvents.map((e) => e.minTime || e.maxTime).filter(Boolean).sort()[0],
      summaryMaxTime: meaningfulEvents.map((e) => e.maxTime || e.minTime).filter(Boolean).sort().slice(-1)[0],
    };
    if (schedule.events !== undefined) {
      // Full schedule with events: persist detail
      updates[`/customSchedules/${userid}/${schedule.key}`] = encrypt(summary);
    }
    // Always update the lightweight summary (strips events but keeps metadata)
    const { events: _events, ...scheduleSummary } = summary;
    updates[`/customScheduleSummaries/${userid}/${schedule.key}`] = encrypt(scheduleSummary);
    await this.update(updates);
  };

  /** Decodes the all-users nested structure returned from /customSchedules/ */
  private customSchedulesUpdatedWrapper = (data: any) => {
    // flatten it out to return all users schedules in a single array
    const returnData: Record<string, ICustomSchedule> = {};
    if (data) {
      Object.keys(data).forEach((user) => {
        Object.keys(data[user]).forEach((key) => {
          returnData[key] = decrypt(data[user][key], {} as ICustomSchedule) as ICustomSchedule;
          returnData[key].userid = user;
          if (this.userCache)
            returnData[key].username = this.userCache[user]?.username ?? "Unknown";
        });
      });
    }
    return returnData;
  };

  /** Decodes the flat per-user structure returned from /customSchedules/{userid}/ */
  private ownCustomSchedulesWrapper = (userid: string, data: any): Record<string, ICustomSchedule> => {
    const returnData: Record<string, ICustomSchedule> = {};
    if (data) {
      Object.keys(data).forEach((key) => {
        returnData[key] = decrypt(data[key], {} as ICustomSchedule) as ICustomSchedule;
        returnData[key].userid = userid;
        if (this.userCache)
          returnData[key].username = this.userCache[userid]?.username ?? "Unknown";
      });
    }
    return returnData;
  };

  /**
   * Subscribe to the logged-in user's own schedules only.
   * Other users' published schedules are fetched on-demand via fetchPublishedSchedules().
   */
  public snapAndSubCustomSchedules = (
    userid: string,
    customSchedulesUpdated: (
      customSchedules: Record<string, ICustomSchedule>
    ) => void
  ): Unsubscribe => {
    const customScheduleRef = ref(this.database, `/customSchedules/${userid}/`);
    return this.on(customScheduleRef, data => customSchedulesUpdated(this.ownCustomSchedulesWrapper(userid, data)));
  };

  public snapshotCustomSchedules = (
    userid: string
  ): Promise<Record<string, ICustomSchedule>> => {
    return this.getTableSnap<Record<string, ICustomSchedule>>(
      `/customSchedules/${userid}/`
    ).then(data => this.ownCustomSchedulesWrapper(userid, data));
  };

  /**
   * One-time fetch of published schedules from all users, excluding the current user's own.
   * Call this lazily when the user opens the schedule browser, not on startup.
   */
  public fetchPublishedSchedules = async (currentUserId: string): Promise<Record<string, ICustomSchedule>> => {
    const data = await this.getTableSnap<any>("/customScheduleSummaries/", true);
    const all = this.customSchedulesUpdatedWrapper(data);
    const published: Record<string, ICustomSchedule> = {};
    Object.keys(all).forEach(key => {
      const s = all[key];
      if (s.published && s.userid !== currentUserId) {
        published[key] = s;
      }
    });
    return published;
  };

  public removeCustomSchedule = async (
    userid: string,
    key: string
  ): Promise<void> => {
    // Atomically delete both detail and summary
    const deletions: Record<string, null> = {
      [`/customSchedules/${userid}/${key}`]: null,
      [`/customScheduleSummaries/${userid}/${key}`]: null,
    };
    await update(ref(this.database), deletions);
  };

  /** One-time snap of lightweight summaries (no events) for a user. */
  public snapshotCustomScheduleSummaries = (
    userid: string
  ): Promise<Record<string, ICustomSchedule>> => {
    return this.getTableSnap<Record<string, ICustomSchedule>>(
      `/customScheduleSummaries/${userid}/`
    ).then(data => this.ownCustomSchedulesWrapper(userid, data));
  };

  /** Real-time subscription to lightweight summaries (no events). */
  public snapAndSubCustomScheduleSummaries = (
    userid: string,
    summariesUpdated: (summaries: Record<string, ICustomSchedule>) => void
  ): Unsubscribe => {
    const summaryRef = ref(this.database, `/customScheduleSummaries/${userid}/`);
    return this.on(summaryRef, data => summariesUpdated(this.ownCustomSchedulesWrapper(userid, data)));
  };

  /** Fetch the full detail (with events) for a single schedule key. */
  public fetchCustomScheduleDetail = async (
    userid: string,
    key: string
  ): Promise<ICustomSchedule | undefined> => {
    const data = await this.getTableSnap<string>(`/customSchedules/${userid}/${key}`);
    if (!data) return undefined;
    const schedule = decrypt(data, {} as ICustomSchedule) as ICustomSchedule;
    schedule.userid = userid;
    if (this.userCache) schedule.username = this.userCache[userid]?.username ?? "Unknown";
    return schedule;
  };

  /**
   * Migration: if the user has no summaries yet but has detail records,
   * generate summaries from them. Subsequent calls are no-ops.
   */
  public migrateCustomScheduleSummaries = async (
    userid: string,
    options?: { includeMetadataBackfill?: boolean }
  ): Promise<void> => {
    const includeMetadataBackfill = options?.includeMetadataBackfill ?? true;
    const existing = await this.getTableSnap<Record<string, ICustomSchedule>>(`/customScheduleSummaries/${userid}/`);
    const hasExistingSummaries = Boolean(existing && Object.keys(existing).length > 0);
    const needsMetadataBackfill = Boolean(
      includeMetadataBackfill &&
      existing && Object.values(existing).some((schedule) => {
        const decrypted = decrypt(schedule as unknown as string, {} as ICustomSchedule) as ICustomSchedule;
        return decrypted.eventCount === undefined;
      })
    );
    if (hasExistingSummaries && !needsMetadataBackfill) return;
    const detail = await this.snapshotCustomSchedules(userid);
    if (!detail || Object.keys(detail).length === 0) return;
    const updates: Record<string, unknown> = {};
    Object.values(detail).forEach((schedule) => {
      const meaningfulEvents = (schedule.events ?? []).filter(
        (e) => Boolean(e.name?.trim() || e.minTime || e.maxTime)
      );
      const summary: ICustomSchedule = {
        ...schedule,
        eventCount: meaningfulEvents.length,
        summaryMinTime: meaningfulEvents.map((e) => e.minTime || e.maxTime).filter(Boolean).sort()[0],
        summaryMaxTime: meaningfulEvents.map((e) => e.maxTime || e.minTime).filter(Boolean).sort().slice(-1)[0],
      };
      const { events: _events, ...scheduleSummary } = summary;
      updates[`/customScheduleSummaries/${userid}/${schedule.key}`] = encrypt(scheduleSummary);
    });
    await this.update(updates);
  };

  public clearOldProofRequests = async (userid: string): Promise<void> => {
    // Proof request entries are transient signal values (boolean `true`) used only
    // to wake the client during the current session. Wipe them all on startup so
    // stale signals from previous sessions cannot trigger false callbacks.
    await this.remove(ref(this.database, `/proofRequests/${userid}`));
  };
  public removeShoppingRequest = (
    userid: string,
    key: string
  ): Promise<void> => {
    const record = new CacheRecord<IShoppingRequest>(ShoppingItemPath, this);
    return record.remove(userid, key);
  };
  public snapshotShoppingRequests = (
    userid: string
  ): Promise<Record<string, IShoppingRequest>> => {
    const record = new CacheRecord<IShoppingRequest>(ShoppingItemPath, this);
    return record.snapshot(userid);
  };
  public snapAndSubShoppingRequests = (
    userid: string,
    shoppingRequestsUpdated: (
      shoppingRequests: Record<string, IShoppingRequest>
    ) => void
  ): Unsubscribe => {
    const record = new CacheRecord<IShoppingRequest>(ShoppingItemPath, this);
    return record.snapAndSub(userid, shoppingRequestsUpdated);
  };
  public addShoppingItem = async (
    userid: string,
    item: IShoppingRequest
  ): Promise<void> => {
    const record = new CacheRecord<IShoppingRequest>(ShoppingItemPath, this);
    await record.add(userid, item);
  };

  public removeTodo = (userid: string, key: string): Promise<void> => {
    const record = new CacheRecord<ITodoItem>(TodoItemPath, this);
    return record.remove(userid, key);
  };

  public snapshotTodo = (
    userid: string
  ): Promise<Record<string, ITodoItem>> => {
    const record = new CacheRecord<ITodoItem>(TodoItemPath, this);
    return record.snapshot(userid);
  };

  public snapAndSubTodo = (
    userid: string,
    todosUpdated: (todos: Record<string, ITodoItem>) => void
  ): Unsubscribe => {
    const record = new CacheRecord<ITodoItem>(TodoItemPath, this);
    return record.snapAndSub(userid, todosUpdated);
  };

  public addTodo = async (userid: string, item: ITodoItem): Promise<void> => {
    const record = new CacheRecord<ITodoItem>(TodoItemPath, this);
    await record.add(userid, item);
  };

  // ── Wizard Templates ──────────────────────────────────────────────────────

  public addWizardTemplate = async (userid: string, item: IWizardTemplate): Promise<void> => {
    const record = new CacheRecord<IWizardTemplate>(WizardTemplatePath, this);
    await record.add(userid, item);
  };

  public removeWizardTemplate = (userid: string, key: string): Promise<void> => {
    const record = new CacheRecord<IWizardTemplate>(WizardTemplatePath, this);
    return record.remove(userid, key);
  };

  public snapAndSubWizardTemplates = (
    userid: string,
    cb: (items: Record<string, IWizardTemplate>) => void
  ): Unsubscribe => {
    const record = new CacheRecord<IWizardTemplate>(WizardTemplatePath, this);
    return record.snapAndSub(userid, cb);
  };

  // ── Outfit ratings ────────────────────────────────────────────────────────
  // Firebase key chars that are not permitted: . # $ [ ] /
  private sanitizeRatingKey = (key: string): string =>
    key.replace(/[.#$[\]/]/g, '_');

  public saveOutfitRating = async (
    userid: string,
    taskKey: string,
    optionKey: string,
    rating: ITaskRating
  ): Promise<void> => {
    const safeOption = this.sanitizeRatingKey(optionKey);
    const updates: Record<string, unknown> = {};
    updates[`outfitRatings/${userid}/${taskKey}/${safeOption}`] = rating;
    await this.update(updates);
  };

  public getOutfitRatingsForTask = async (
    userid: string,
    taskKey: string
  ): Promise<Record<string, ITaskRating> | null> => {
    return this.getTableSnap<Record<string, ITaskRating> | null>(
      `outfitRatings/${userid}/${taskKey}`
    );
  };

  // ── Schedule ratings ────────────────────────────────────────────────────
  public saveScheduleRating = async (
    scheduleKey: string,
    userid: string,
    rating: ITaskRating
  ): Promise<void> => {
    const safeKey = this.sanitizeRatingKey(scheduleKey);
    const updates: Record<string, unknown> = {};
    updates[`scheduleRatings/${safeKey}/${userid}`] = rating;
    await this.update(updates);
  };

  public getScheduleRatings = async (
    scheduleKey: string
  ): Promise<Record<string, ITaskRating> | null> => {
    const safeKey = this.sanitizeRatingKey(scheduleKey);
    return this.getTableSnap<Record<string, ITaskRating> | null>(
      `scheduleRatings/${safeKey}`
    );
  };

  // ── Event ratings ────────────────────────────────────────────────────────
  public saveEventRating = async (
    eventKey: string,
    userid: string,
    rating: ITaskRating,
    scheduleKey?: string
  ): Promise<void> => {
    const safeKey = this.sanitizeRatingKey(eventKey);
    const safeScheduleKey = scheduleKey ? this.sanitizeRatingKey(scheduleKey) : undefined;
    const updates: Record<string, unknown> = {};
    if (safeScheduleKey) {
      updates[`eventRatingsBySchedule/${safeScheduleKey}/${safeKey}/${userid}`] = rating;
    } else {
      // Legacy path for callers that are not schedule-scoped.
      updates[`eventRatings/${safeKey}/${userid}`] = rating;
    }
    await this.update(updates);
  };

  public getEventRatings = async (
    eventKey: string,
    scheduleKey?: string
  ): Promise<Record<string, ITaskRating> | null> => {
    const safeKey = this.sanitizeRatingKey(eventKey);
    const safeScheduleKey = scheduleKey ? this.sanitizeRatingKey(scheduleKey) : undefined;
    if (safeScheduleKey) {
      return this.getTableSnap<Record<string, ITaskRating> | null>(
        `eventRatingsBySchedule/${safeScheduleKey}/${safeKey}`
      );
    }
    // Legacy path for callers that are not schedule-scoped.
    return this.getTableSnap<Record<string, ITaskRating> | null>(`eventRatings/${safeKey}`);
  };
}

class CacheRecord<T extends IKeyedItem> {
  path: string;
  databaseRef: Database;

  constructor(p: string, database: Database) {
    this.path = p;
    this.databaseRef = database;
  }

  add = async (userid: string | undefined, item: T): Promise<void> => {
    const updates: Record<string, unknown> = {};
    const json = encrypt(item);
    if (userid) updates[`/${this.path}/${userid}/${item.key}`] = json;
    else updates[`/${this.path}/${item.key}`] = json;
    await this.databaseRef.update(updates);
  };
  remove = async (userid: string | undefined, key: string): Promise<void> => {
    if (!userid)
      await this.databaseRef.remove(
        ref(this.databaseRef.database, `/${this.path}/${key}`)
      );
    return await this.databaseRef.remove(
      ref(this.databaseRef.database, `/${this.path}/${userid}/${key}`)
    );
  };
  snapshot = (userid: string | undefined): Promise<Record<string, T>> => {
    if (!userid)
      return this.databaseRef.getTableSnap<Record<string, T>>(`/${this.path}`);
    return this.databaseRef.getTableSnap<Record<string, T>>(
      `${this.path}/${userid}`
    );
  };
  snapAndSub = (
    userid: string | undefined,
    callback: (callback: Record<string, T>) => void
  ): Unsubscribe => {
    if (!userid) {
      const requestsRef = ref(this.databaseRef.database, `/${this.path}`);
      return this.databaseRef.on(requestsRef, callback);
    } else {
      const requestsRef = ref(
        this.databaseRef.database,
        `/${this.path}/${userid}`
      );
      return this.databaseRef.on(requestsRef, callback);
    }
  };
}
